export interface Course {
  id: string
  title: string
  subtitle: string
  category: string
  description: string
  fullDescription: string
  image?: string
  video?: string
  icon?: string
  duration: string
  level: string
  students: string
  modules: string[]
  lessons: string
  author?: string
  authorTitle?: string
  authorAvatar?: string
  relatedBlog?: {
    href: string
    label: string
  }
  curriculum: {
    section: string
    description: string
    lessons: number
    duration: string
    lessonDetails?: {
      title: string
      duration: string
    }[]
  }[]
}

export const courses: Course[] = [
  {
    id: 'mastering-ai-agents',
    title: 'Mastering AI Agents – The Builder\'s Deep Dive Into Agent Loops, Tools, Context & Memory',
    subtitle: 'Mastering AI Agents: The Builder\'s Deep Dive',
    category: 'AI Agent Development',
    description: 'Go from using AI to building it. Master the six pillars behind every production agent system — with research-backed lessons and real architecture teardowns.',
    fullDescription: 'Most engineers use AI agents as black boxes. When they break, there\'s no mental model for why. This course gives you the six-pillar framework — Agent Loop, Tool System, Context Engineering, Memory, Multi-Agent, and Harness Engineering — that explains every production agent system. Each lesson starts with a real problem, walks through how Claude Code, Manus, and OpenClaw solved it, and extracts reusable design principles. Built for engineers who want depth, founders who need to evaluate AI architecture, and vibe coders ready to understand what\'s underneath.',
    icon: '🧠',
    image: '/thumbnails/mastering-agents.png',
    duration: '10+ hours',
    level: 'Intermediate',
    students: '500+',
    lessons: '50',
    relatedBlog: {
      href: '/blog/ai-agents',
      label: 'free AI Agents 2026 guide',
    },
    modules: [
      'The Six Pillars Framework',
      'Agent Loop Architecture',
      'Tool System Design & MCP',
      'Context Engineering',
      'Memory Systems',
      'Multi-Agent Orchestration',
      'Evaluation & Testing',
      'Cost & Latency Engineering',
      'Harness Engineering',
      'Framework Evaluation',
    ],
    curriculum: [
      {
        section: 'Chapter 0: The Fear (and Why It\'s Misplaced)',
        description: 'The honest data on AI replacing engineers, the Jevons Paradox, the vibe coder\'s ceiling, and the job on the other side of it.',
        lessons: 1,
        duration: '15m',
        lessonDetails: [
          { title: '"Will AI Replace Me?" — Let\'s Actually Think This Through', duration: '15m' },
        ]
      },
      {
        section: 'Chapter 1: Cognitive Calibration — The Six Pillars',
        description: 'The mental model you\'ll use for everything else. One agent call activates six systems, with a hands-on exercise to map any product. Plus the LLM mechanics that explain every engineering decision.',
        lessons: 4,
        duration: '1h 5m',
        lessonDetails: [
          { title: 'One Agent Call, Six Systems: What Actually Happens Under the Hood', duration: '15m' },
          { title: 'From ChatBot to Agent: The while(true) That Changes Everything', duration: '18m' },
          { title: 'The LLM Mechanics You Can\'t Skip (But Nothing More)', duration: '12m' },
          { title: 'Why Production Agents Don\'t Use LangChain (And What They Use Instead)', duration: '15m' },
        ]
      },
      {
        section: 'Chapter 2: Agent Loop — The Heartbeat',
        description: 'Streaming architecture, production retry systems, loop detection, token budgets, and building a production-grade agent loop from scratch.',
        lessons: 5,
        duration: '1h 25m',
        lessonDetails: [
          { title: 'Your Agent Hangs for 30 Seconds With No Output. What Went Wrong?', duration: '18m' },
          { title: 'The Connection Died Ten Minutes Ago. Your Agent Still Says "Generating..."', duration: '15m' },
          { title: 'The API Returns a 500. Then a 429. Then a Timeout. Design the Retry System.', duration: '18m' },
          { title: 'Your Agent Burned $8 in Tokens and Accomplished Nothing. Now What?', duration: '20m' },
          { title: 'Build Checkpoint: Production-Grade Agent Loop From Scratch', duration: '12m' },
        ]
      },
      {
        section: 'Chapter 3: Tool System — Hands and Feet',
        description: 'Function calling internals, the tool explosion problem, MCP in production, Skills as knowledge distribution, and permission system design.',
        lessons: 8,
        duration: '2h 15m',
        lessonDetails: [
          { title: 'Function Calling From First Principles: The Model Doesn\'t Understand Your Tools', duration: '20m' },
          { title: 'The Tool Execution Pipeline: 7 Steps From Intent to Action', duration: '18m' },
          { title: 'Your Agent Has 50 Tools. It Picks the Wrong One 30% of the Time.', duration: '22m' },
          { title: 'MCP: The Protocol That Was Supposed to Solve Everything', duration: '18m' },
          { title: 'MCP Connects Tools. ACP Connects Clients. A2A Connects Agents.', duration: '15m' },
          { title: 'Skills: The Knowledge Layer MCP Can\'t Provide', duration: '18m' },
          { title: 'The AI Ran rm -rf on Production. Design the Permission System.', duration: '22m' },
          { title: 'Build Checkpoint: Tool System With Dynamic Loading and Permissions', duration: '12m' },
        ]
      },
      {
        section: 'Chapter 4: Context Engineering — The Real Moat',
        description: 'The five dimensions of context management, system prompt engineering, compression strategies, RAG, JIT loading, and the compiled knowledge base.',
        lessons: 9,
        duration: '2h 30m',
        lessonDetails: [
          { title: 'The Five Dimensions of Context Engineering', duration: '15m' },
          { title: 'Your System Prompt Is 4K Tokens and the Agent Ignores Half. Why?', duration: '15m' },
          { title: 'Tool Call #47: The Agent Forgot What You Said on Call #3. Fix This.', duration: '18m' },
          { title: 'KV Cache, Prompt Cache, Semantic Cache — What\'s the Difference?', duration: '15m' },
          { title: 'Just-In-Time Context: Why Pre-Loading Everything Is an Anti-Pattern', duration: '15m' },
          { title: 'RAG Done Right: From Document Dump to Agent-Usable Knowledge', duration: '18m' },
          { title: '"Semantically Similar" ≠ "Actually Useful" — Your Retrieval Is Broken', duration: '15m' },
          { title: 'The Compiled Knowledge Base: Making Knowledge Accumulate, Not Rot', duration: '15m' },
          { title: 'Build Checkpoint: Context Management for 50+ Tool-Call Sessions', duration: '12m' },
        ]
      },
      {
        section: 'Chapter 5: Memory — Persistence Across Sessions',
        description: 'File-based vs database memory, the five ways agent memory fails, and building cross-session memory from scratch.',
        lessons: 3,
        duration: '45m',
        lessonDetails: [
          { title: 'The Agent Used npm After You Said "Always Use pnpm." Why?', duration: '15m' },
          { title: 'The Five Ways Agent Memory Fails — and the Engineering Fix for Each', duration: '18m' },
          { title: 'Build Checkpoint: Cross-Session Memory With Both Approaches', duration: '12m' },
        ]
      },
      {
        section: 'Chapter 6: Multi-Agent — Divide Context, Not Roles',
        description: 'Why role-playing fails, parent-child context isolation, agent swarms, worktree isolation, and building a multi-agent system.',
        lessons: 5,
        duration: '1h 15m',
        lessonDetails: [
          { title: 'One Agent as PM, One as Developer — Why This Almost Never Works', duration: '15m' },
          { title: '50K-Line Codebase: One Agent or Two?', duration: '15m' },
          { title: 'Agent Swarm: When You Genuinely Need N Agents', duration: '15m' },
          { title: 'Worktree Isolation: Multi-Agent With File System Safety', duration: '15m' },
          { title: 'Build Checkpoint: Parent-Child Agent System', duration: '12m' },
        ]
      },
      {
        section: 'Chapter 7: Evaluation & Testing — How Do You Know It Works?',
        description: 'Why agent testing is fundamentally different, building eval suites, and regression testing for non-deterministic systems.',
        lessons: 3,
        duration: '45m',
        lessonDetails: [
          { title: 'Your Agent Passes the Demo. It Fails for 40% of Real Users.', duration: '15m' },
          { title: 'Building an Eval Suite: Offline, Online, and Human-in-the-Loop', duration: '18m' },
          { title: 'It Worked Yesterday Is Not a Test Strategy', duration: '12m' },
        ]
      },
      {
        section: 'Chapter 8: Cost & Latency Engineering',
        description: 'Token economics, model routing, latency budgets, and making agents affordable at scale.',
        lessons: 2,
        duration: '30m',
        lessonDetails: [
          { title: 'Your Agent Costs $2.40 Per Task. Your Margin Is $0.50.', duration: '15m' },
          { title: '45-Second Response Time. 38 Seconds Wasted.', duration: '15m' },
        ]
      },
      {
        section: 'Chapter 9: Harness Engineering — The Production Shell',
        description: 'Observability, deployment, security, and the 98.4% of infrastructure that makes the 1.6% of AI logic safe to run.',
        lessons: 6,
        duration: '1h 30m',
        lessonDetails: [
          { title: 'Harness \u2260 Framework: What Is This Layer', duration: '15m' },
          { title: 'Something Went Wrong. You Have No Logs.', duration: '15m' },
          { title: 'Deployment: Where Does the Agent Run', duration: '15m' },
          { title: 'The OpenClaw RCE Post-Mortem: Security', duration: '15m' },
          { title: 'The Permission System Was On. The Attack Worked Anyway.', duration: '15m' },
          { title: 'How OpenAI and Anthropic Actually Build Their Harness', duration: '18m' },
        ]
      },
      {
        section: 'Chapter 10: Frameworks — Through the Six-Pillar Lens',
        description: 'Evaluate LangGraph, CrewAI, Mastra, Vercel AI SDK — not as tutorials, but through the framework you now own.',
        lessons: 1,
        duration: '15m',
        lessonDetails: [
          { title: 'LangGraph, CrewAI, Mastra, Vercel AI SDK — Evaluate Them Yourself', duration: '15m' },
        ]
      },
      {
        section: 'Capstone: Design Your Own Agent',
        description: 'Choose a real problem. Make every trade-off explicit. Defend every decision using the Six Pillars.',
        lessons: 2,
        duration: '30m',
        lessonDetails: [
          { title: 'Design Your Own Agent Architecture', duration: '18m' },
          { title: 'The Engineer AI Can\'t Replace', duration: '12m' },
        ]
      },
      {
        section: 'Download: Practice Code & Exercises',
        description: 'Downloadable code labs and practice exercises covering all chapters.',
        lessons: 1,
        duration: '',
        lessonDetails: [
          { title: 'Code Lab (All Chapters)', duration: '' },
        ]
      },
    ]
  },
  {
    id: 'bolt-new-growth-case-study',
    title: 'Bolt.new Growth Case Study – From 0 to 1M+ Messages in 7 Days | CEO Insights & Best Practices',
    subtitle: 'Bolt.new Growth Case Study with CEO Eric',
    category: 'AI Product Launches / Case Studies',
    description: 'Discover how Bolt.new scaled from 0 to 1M+ messages in 7 days and learn best practices from CEO Eric for AI founders and product builders.',
    fullDescription: 'Join us for an exclusive Expert Talk with Bolt.new CEO Eric as he walks through one of the fastest product launches in AI history — growing from 0 to 1 million+ messages in just 7 days. Learn the growth strategies, product-led tactics, and founder frameworks that made it possible. We\'ll also dive into how Bolt.new used AI infrastructure, leveraged viral loops, and implemented early feedback loops. Whether you\'re building a startup or launching a new AI tool, this talk is packed with actionable takeaways.',
    icon: '⚡',
    image: '/thumbnails/boltnew.jpg',
    duration: '49m',
    level: 'All levels',
    students: '500+',
    lessons: '1',
    modules: [
      'Growth Strategies & Product-Led Tactics',
      'Founder Frameworks & Best Practices',
      'AI Infrastructure & Viral Loops',
      'Early Feedback Implementation'
    ],
    curriculum: []
  },
  {
    id: 'ai-product-journey-ethan-trang',
    title: 'AI Product Journey with Ethan Trang – From Zero Coding to Launching 2 AI Tools in 12 Months',
    subtitle: 'Ethan Trang\'s AI Product Journey and Coding Workflow',
    category: 'AI Builder Journeys / Young Founders',
    description: 'Explore Ethan Trang\'s journey from zero coding knowledge to launching two AI products in a year—perfect for student founders and AI builders.',
    fullDescription: 'In this inspiring Expert Talk, we sit down with Ethan Trang, a 19-year-old self-taught AI builder who went from zero programming knowledge to launching two AI products in 12 months, with one nearly acquired. Ethan shares his step-by-step learning path, how he approached student-focused app distribution, and how he reverse-engineered successful platforms by inspecting network requests — all using tools like Cursor. If you\'re just getting started or want to understand how Gen Z is building in AI, this talk is packed with practical insights.',
    icon: '🚀',
    image: '/thumbnails/ethan.jpg',
    duration: '47m',
    level: 'All levels',
    students: '400+',
    lessons: '1',
    modules: [
      'Zero to Coding: Step-by-Step Learning Path',
      'Student-Focused App Distribution Strategies',
      'Reverse Engineering & Network Request Analysis',
      'AI Product Development with Cursor'
    ],
    curriculum: []
  },
  {
    id: 'task-management-cursor-best-practices',
    title: 'Task Management in Cursor – Best Practices from Claude Task Master Creator to 10x Your Workflow',
    subtitle: 'Task Management in Cursor with Claude Task Master Creator',
    category: 'AI Engineering Workflows / Productivity with LLMs',
    description: 'Learn how to embed effective task management inside Cursor and reduce error rates by 10x—featuring Claude Task Master\'s creator.',
    fullDescription: 'In this Expert Talk, we interview the creator of Claude Task Master, an AI-native productivity system built inside Cursor. Discover how they embedded task management directly into the developer workflow, dramatically reducing LLM-related error rates by 10x. We break down the tools, techniques, and workflow integrations that helped make this possible. Whether you\'re using Cursor, building with Claude, or scaling AI agent workflows, this session is packed with practical strategies and advanced debugging tips.',
    icon: '⚙️',
    image: '/thumbnails/taskmaster.jpg',
    duration: '31m',
    level: 'All levels',
    students: '350+',
    lessons: '1',
    modules: [
      'AI-Native Productivity System Design',
      'Embedding Task Management in Developer Workflow',
      'Reducing LLM Error Rates by 10x',
      'Advanced Debugging Tips & Workflow Integration'
    ],
    curriculum: []
  },
  {
    id: 'prompt-engineering-llm-evaluation-helicone',
    title: 'Prompt Engineering & LLM Evaluation with Helicone – How to 10x Prompt Quality and Cut Costs',
    subtitle: 'Prompt Engineering Best Practices with Helicone\'s Founder',
    category: 'LLM Optimization / Prompt Engineering',
    description: 'Learn how to write better, cheaper prompts and optimize LLM workflows—featuring Helicone founder Justin Torre.',
    fullDescription: 'In this Expert Talk, we sit down with Justin Torre, founder of Helicone, the leading open-source LLM observability platform. We dive deep into prompt engineering strategies used by top AI teams, exploring what makes a good prompt, how to systematically improve your LLM pipeline, and where evaluations and testing fit into your workflow. Whether you\'re building AI apps or fine-tuning large models, this session will help you boost output quality while lowering costs—with real-world examples and battle-tested advice.',
    icon: '🎯',
    image: '/thumbnails/helicone.jpg',
    duration: '28m',
    level: 'All levels',
    students: '450+',
    lessons: '1',
    modules: [
      'Prompt Engineering Strategies from Top AI Teams',
      'Systematic LLM Pipeline Improvement',
      'LLM Evaluation & Testing Workflows',
      'Cost Optimization & Quality Enhancement'
    ],
    curriculum: []
  },
  {
    id: 'build-ui-backend-v0-dev-garrett',
    title: '[Advanced] Build Beautiful UI & Full Backend with Garrett – v0.dev Themes, Templates, and Email Setup',
    subtitle: 'Build UI & Backend Using v0.dev with Garrett',
    category: 'Full-Stack AI Development / UI Design Tools',
    description: 'Learn how to build beautiful UIs and full-stack backends using v0.dev—includes themes, database setup, and email workflows.',
    fullDescription: 'In this Expert Talk, full-stack builder Garrett walks through his advanced workflow for building beautiful UIs and robust backends using v0.dev. In this 47-minute deep dive, you\'ll discover: How to use v0.dev themes for instant design quality, best-practice templates for backend and database setup, tips for building stunning interfaces without custom CSS, and how to configure email functionality for real-world applications. This session is ideal for developers looking to accelerate their build process and ship polished AI tools faster.',
    icon: '🎨',
    image: '/thumbnails/garret.jpg',
    duration: '47m',
    level: 'All levels',
    students: '380+',
    lessons: '1',
    modules: [
      'v0.dev Themes for Instant Design Quality',
      'Backend & Database Setup Best Practices',
      'Building Stunning Interfaces Without Custom CSS',
      'Email Configuration for Real-World Applications'
    ],
    curriculum: []
  },
  {
    id: 'research-agent-tdd-cursor-cognosys',
    title: 'Build a Research Agent & Apply TDD in Cursor – Lessons from Cognosys.ai and Otto by Sully',
    subtitle: 'How to Build Research Agents with TDD in Cursor – Insights from Sully',
    category: 'AI Agent Development / LLM Engineering',
    description: 'Learn how to build production-ready research agents and apply Test Driven Development in Cursor—featuring lessons from Cognosys.ai and Otto.',
    fullDescription: 'In this Expert Talk, Sully shares the hard-earned lessons from building a production-grade AI research agent at Cognosys.ai and Otto Grid. You\'ll learn: How to select the right LLM for your use case, tactics used by Cognosys to reduce LLM costs at scale, and how Sully uses a Test Driven Development (TDD) workflow in Cursor to speed up debugging and boost agent quality. This session is packed with practical frameworks, engineering tactics, and real-world takeaways for anyone building or optimizing AI agents.',
    icon: '🔬',
    image: '/thumbnails/sully.jpg',
    duration: '33m',
    level: 'All levels',
    students: '420+',
    lessons: '1',
    modules: [
      'Selecting the Right LLM for Your Use Case',
      'Cost Reduction Tactics at Scale (Cognosys Lessons)',
      'Test Driven Development (TDD) Workflow in Cursor',
      'Debugging & Quality Enhancement for AI Agents'
    ],
    curriculum: []
  },
  {
    id: 'ai-org-graphs-data-insights-crawl4ai',
    title: 'From Data to Insights – Build AI Org Graphs with crawl4ai Author Unclecode (Full Workshop)',
    subtitle: 'AI Org Graphs & Data Processing with crawl4ai by Unclecode',
    category: 'AI Data Engineering / Web Crawling',
    description: 'Learn how to turn LinkedIn data into AI-powered org charts with crawl4ai—featuring advanced prompt engineering and vector similarity techniques.',
    fullDescription: 'Join Unclecode, creator of crawl4ai, for a hands-on workshop on converting crawled web data into organizational insights. You\'ll learn how to process LinkedIn data, generate vector embeddings, and use LLMs to infer org charts. This session covers prompt engineering, cosine similarity, and building an interactive AI-powered graph app in Colab.',
    icon: '🕸️',
    image: '/thumbnails/unclecode.png',
    duration: '2h 16m',
    level: 'All levels',
    students: '300+',
    lessons: '1',
    modules: [
      'Web Data Processing with crawl4ai',
      'Vector Embeddings & LLM Integration',
      'Prompt Engineering for Data Insights',
      'Building Interactive AI-Powered Graph Apps'
    ],
    curriculum: []
  },
  {
    id: 'crawl4ai-web-crawling-for-llms',
    title: 'Crawl4AI – AI-Ready Web Crawling for LLMs, AI Agents, and Data Pipelines with Unclecode',
    subtitle: 'AI-Optimized Web Crawling with crawl4ai by Unclecode',
    category: 'Web Crawling / LLM Data Pipelines',
    description: 'Discover how to use crawl4ai for fast, smart web crawling designed for LLMs, AI agents, and scalable data workflows—no API keys required.',
    fullDescription: 'In this Expert Talk, Unclecode, creator of crawl4ai, shares how to build blazing-fast web crawlers tailored for LLMs and RAG pipelines. Learn how crawl4ai generates Markdown-ready content, manages sessions, and extracts data with heuristic intelligence—all open-source and Docker-ready for deployment.',
    icon: '🔍',
    image: '/thumbnails/unclecode1.png',
    duration: '1h 5m',
    level: 'All levels',
    students: '280+',
    lessons: '1',
    modules: [
      'Blazing-Fast Web Crawlers for LLMs',
      'Markdown-Ready Content Generation',
      'Session Management & Data Extraction',
      'Open-Source Deployment with Docker'
    ],
    curriculum: []
  },
  {
    id: 'automate-web-ai-words-airtop',
    title: 'Automate Any Website with Just Words – Go-to-Market & AI Web Automation with Airtop CEO Amir',
    subtitle: 'Web Automation with Natural Language – Airtop CEO on Go-to-Market Strategy',
    category: 'AI Automation / Go-to-Market for AI Tools',
    description: 'Learn how Airtop.ai enables full web automation using natural language and how they approached go-to-market—featuring CEO Amir.',
    fullDescription: 'In this Expert Talk, Airtop.ai CEO Amir shares how his team built a platform that automates any website using just words. Learn how they handle authentication, captcha solving, visual flows, and compliance (SOC2, HIPAA)—all with natural language input. Amir also dives into go-to-market strategy, challenges in scraping automation, and how Airtop is shaping the future of AI-driven task execution.',
    icon: '🤖',
    image: '/thumbnails/amir.png',
    duration: '55m',
    level: 'All levels',
    students: '220+',
    lessons: '1',
    modules: [
      'Natural Language Web Automation Platform',
      'Authentication, Captcha & Compliance Solutions',
      'Go-to-Market Strategy for AI Tools',
      'Future of AI-Driven Task Execution'
    ],
    curriculum: []
  },
  {
    id: 'cursor-courses',
    title: 'Cursor Full Stack Apps Course – Build Web & Mobile UIs with AI & Real Case Studies',
    subtitle: 'Ultimate Cursor Courses',
    category: 'AI App Development',
    description: 'Learn how to use Cursor AI to build full stack apps faster—ideal for indie hackers, startup founders, and AI developers.',
    fullDescription: 'Master full stack development using Cursor AI, an AI-native IDE that supercharges productivity. In this course, you\'ll explore Cursor workflows, learn how to build end-to-end web and mobile apps, and design sleek, production-ready UIs. With 2 in-depth case studies and real-world examples, you\'ll gain hands-on experience in React, Tailwind CSS, Supabase, and other modern tools. Perfect for developers who want to build smarter and ship faster using AI-powered development.',
    icon: '💻',
    image: '/thumbnails/cursorcourse.png',
    duration: '',
    level: 'All levels',
    students: '1,500+',
    lessons: '33',
    modules: [
      'Cursor AI Fundamentals & Setup',
      'Full Stack Development Workflows',
      'React & Tailwind CSS with AI',
      'Supabase Integration',
      'Case Study 1: Web Application',
      'Case Study 2: Mobile Application',
      'Production Deployment',
      'AI-Powered Development Best Practices'
    ],
    curriculum: [
      {
        section: 'Cursor Processes',
        description: 'Master essential Cursor workflows and processes for efficient AI-powered development.',
        lessons: 5,
        duration: '2h 30m',
        lessonDetails: [
          { title: 'Write PRD doc', duration: '30m' },
          { title: 'Break down PRD into tasks', duration: '25m' },
          { title: 'Test driven development', duration: '45m' },
          { title: 'Memory bank', duration: '35m' },
          { title: 'Cursor rules & Custom modes (WIP)', duration: '35m' },
        ]
      },
      {
        section: 'Build Full Stack Web App',
        description: 'Build a complete full-stack web application using Cursor AI from setup to deployment.',
        lessons: 6,
        duration: '3h 45m',
        lessonDetails: [
          { title: 'Setup', duration: '30m' },
          { title: 'Build frontend', duration: '60m' },
          { title: 'Setup Auth', duration: '45m' },
          { title: 'Build Backend', duration: '50m' },
          { title: 'Deploy', duration: '25m' },
          { title: 'Tips & resources', duration: '15m' },
        ]
      },
      {
        section: 'Build Mobile App',
        description: 'Create a mobile application using Cursor AI with responsive design and mobile-first approach.',
        lessons: 5,
        duration: '2h 15m',
        lessonDetails: [
          { title: 'Mobile project setup', duration: '25m' },
          { title: 'Mobile UI components', duration: '35m' },
          { title: 'Mobile navigation', duration: '30m' },
          { title: 'Mobile optimization', duration: '25m' },
          { title: 'Mobile deployment', duration: '20m' },
        ]
      },
      {
        section: 'Case Study: Build AI PDF Converter',
        description: 'End-to-end case study building an AI-powered PDF converter application with Cursor.',
        lessons: 4,
        duration: '2h',
      },
      {
        section: 'Case Study: Build AI Reddit Analytics Platform',
        description: 'End-to-end case study building an AI Reddit analytics platform for market research.',
        lessons: 3,
        duration: '1h 30m',
      },
      {
        section: 'Cursor Templates',
        description: 'Reusable Cursor templates and starter projects for rapid prototyping.',
        lessons: 4,
        duration: '1h 30m',
      },
      {
        section: 'Cursor 2026: Modern Workflows',
        description: 'The latest Cursor features and workflows for 2026: rules, agent mode, background agents, MCP, auto-run, and skills.',
        lessons: 6,
        duration: '2h',
        lessonDetails: [
          { title: 'Cursor Rules', duration: '20m' },
          { title: 'Agent Mode', duration: '20m' },
          { title: 'Background Agents', duration: '20m' },
          { title: 'MCP', duration: '20m' },
          { title: 'Auto-Run Mode', duration: '20m' },
          { title: 'Skills', duration: '20m' },
        ]
      },
    ]
  },
  {
    id: 'live-ai-workshops',
    title: 'Weekly Live AI Workshops - Live Sessions + Recorded Library',
    subtitle: 'Weekly Live AI Workshops',
    category: 'AI Workshop',
    description: 'Join Jason\'s weekly live AI workshops featuring new tools, workflows, and community project showcases - plus the full library of past recorded sessions.',
    fullDescription: 'Every week Jason and the AI Builder Club community go live to share new AI tools, agent workflows, real-world builds, and answer your questions in interactive sessions. Can\'t make it live? The complete archive of past workshops and recaps is below, so you can catch up anytime and reuse the techniques in your own projects.',
    icon: '🎯',
    image: '/thumbnails/weeklylive.png',
    video: '/videos/live-ai-workshops-preview.mp4',
    duration: '1 hour per week',
    level: 'All levels',
    students: '1000+',
    lessons: 'Continuous',
    modules: [
      'Jev: Decisions Instead of Text, at a Fraction of LLM Cost',
      'Realistic AI UGC: Characters That Don\'t Look AI',
      'Making High Quality Launch Videos with HyperFrames',
      'Chain of Custody for AI Work, and a Week on Grok Bot',
      'Ara: Cloud Coding, Verified PRs, and Token Maxing',
      'Graph Engineering Without a Framework',
      'Vibe Coding From Your Phone, and a Loop on Reddit',
      'Running a Fleet of Parallel Agents with Orca',
      'Anatomy of a Good Loop + Hermes Personal Automation',
      'Customizable Agent Harnesses with Pi',
      'Codebase Memory MCP + Postia Teardown',
      'Crabbox: Isolated Sandboxes for Parallel Agent Testing',
      'Loop Engineering Best Practices',
      'Harness Engineering for Agents',
      'AI Wiki Toolkit: Agent Memory',
      'OpenAI Symphony + Automated Testing',
      'OpenClaw Production Workflows',
      'Multi-AI Agent Workflows',
      'Context Engineering & File-Based Memory',
      'Interactive Q&A Sessions'
    ],
    curriculum: [
      {
        section: 'How the Live Sessions Work',
        description: 'Each week splits into project & knowledge sharing followed by an open Q&A.',
        lessons: 2,
        duration: '1h',
        lessonDetails: [
          { title: 'Project & Knowledge Sharing', duration: '40m' },
          { title: 'Questions & Answers', duration: '20m' },
        ]
      },
      {
        section: '09/20 - Jev: Decisions Instead of Text, at a Fraction of LLM Cost',
        description: 'Jason on Jev, a model that returns a probability for every option instead of generating text. Where it prices out against Haiku and the cheapest LLMs, the three output types and the confidence threshold that routes to a human, and the fraud scan, buying signal filter and launch filter he already runs with it in production.',
        lessons: 9,
        duration: '1h',
        lessonDetails: [
          { title: 'What Jev is: a decision model from a ChatGPT co-creator that scores every option in one pass instead of generating tokens, 5 to 7x cheaper than cheap LLMs', duration: '' },
          { title: 'Three output types, choice, true or false, and score, each with a confidence number, so your code can send anything under 60% to a human', duration: '' },
          { title: 'The limits: a 32K context, so map-reduce anything bigger, and no text or code output, so pair it with a small language model when you need words', duration: '' },
          { title: 'Prompting is config, not prose: name, type, instructions and criteria, and how one instruction line moves a bug complaint from billing to engineering', duration: '' },
          { title: 'Production on SuperDesign and treg: a fraud and bot scan over every signup every few minutes, plus email checks and enrichment, about 85% cheaper than Clay', duration: '' },
          { title: 'Buying signals: classify the commenters on a LinkedIn post to find the decision makers, and a filter that separates organic X launches from boosted ones', duration: '' },
          { title: 'Other fits: browser-use picking a DOM target before any text, internal link mapping across hundreds of pages in a minute, an 18 cent Zillow search, pg-jev', duration: '' },
          { title: 'System 1 and System 2: a 10-minute log cron where Jev classifies known issues, unknown anomalies go to Claude Code, and it rewrites a JSON config, not code', duration: '' },
          { title: 'Generating Jev code: paste the advanced primitives doc into the coding agent first, and the estimate that about 90% of loop steps never need a big model', duration: '' },
        ]
      },
      {
        section: '09/13 - Realistic AI UGC: Characters That Don\'t Look AI',
        description: 'Video of a real person is easy now. A brand new person who looks real is the hard part, so this session builds one: a locked JSON character prompt that fights the image model\'s defaults, the model that draws the most believable face, and Seedance 2.5 with a real audio reference.',
        lessons: 7,
        duration: '1h',
        lessonDetails: [
          { title: 'How UGC campaigns actually run: new accounts, $20 a video plus a views bonus, and why AI is the obvious way to scale them', duration: '' },
          { title: 'Where AI UGC breaks: one-shot video and real-person video are easy, a new character with a chosen vibe is not', duration: '' },
          { title: 'Why every AI face looks the same: image models regress to a polished average, and a JSON template that pins every facial variable', duration: '' },
          { title: 'The drift table: model default vs real deviation for eyes, face, body, skin and hair, plus why 4K and ultra detail now make it look fake', duration: '' },
          { title: 'Same prompt, five image models, a blind vote: Gemini 3 Pro draws the most believable person, GPT Image 2.5 looks too good', duration: '' },
          { title: 'Getting Seedance 2.5 to accept a realistic character: BytePlus org verification vs PiAPI vs Higgsfield, $4.39 to $9.50 for the same 19 seconds', duration: '' },
          { title: 'Fixing the robotic voice with an audio reference, and the format that keeps cost down: AI face only on the 10-second hook, then a screen-recorded demo, so each video stays around $3 to $4', duration: '' },
        ]
      },
      {
        section: '09/06 - Making High Quality Launch Videos with HyperFrames',
        description: 'Jason builds a 78-second launch video with HyperFrames from scratch. Why HTML gives an agent a timeline it can check frame by frame, and why the first few compositions need hand direction before the rest can copy them.',
        lessons: 7,
        duration: '1h',
        lessonDetails: [
          { title: 'Why HTML video: the agent can pull any single frame and check it, so it can find its own mistakes, unlike the After Effects API or a JSON schema', duration: '' },
          { title: 'The full build of a treg Codex plugin launch video, from storyboard to composition-by-composition art direction', duration: '' },
          { title: 'By default the model hands you a slide deck; three to four rounds of feedback per composition is what makes it shippable', duration: '' },
          { title: 'Art direct three, copy seven: dial in the first compositions by hand, then "same quality and UI treatment" lands the rest near first try', duration: '' },
          { title: 'Pixel-perfect UI from one pasted screenshot, and camera vocabulary over design vocabulary in your feedback', duration: '' },
          { title: 'Letting the animation carry the pitch: contact rows resolving email vendor by vendor so the viewer sees the waterfall', duration: '' },
          { title: 'Past launch videos as a reusable component library, and simplify as a standing instruction', duration: '' },
        ]
      },
      {
        section: '08/30 - Chain of Custody for AI Work, and a Week on Grok Bot',
        description: 'Member session on proving AI-made work is real when anyone can generate a portfolio in a day: a voice clone grounded in cited facts, and a phone number an agent can actually receive verification codes on. Then Jason on a week of Grok Bot as his daily driver: local and cloud in one agent, and a Cursor VM per task.',
        lessons: 8,
        duration: '1h',
        lessonDetails: [
          { title: 'The slide title says it: generation went to zero, verification did not. A cited, role-gated voice clone of yourself as the first touchpoint', duration: '' },
          { title: 'The stack: Qwen3 TTS open weights on Modal, with an LLM writing the voice-cloning recipe from one voice note', duration: '' },
          { title: 'Why VoIP numbers fail one-time-code checks, and what a carrier-backed number changes for agents that need to receive codes', duration: '' },
          { title: 'Grok Bot\'s hybrid computer: one shared cloud machine, plus any laptop with the app installed as a machine the agent can use', duration: '' },
          { title: 'Engineering tasks spawn a Cursor cloud agent with its own VM and dev server, and every PR opens into that environment', duration: '' },
          { title: 'Memory tiers (profile, log, note), send-to-agent as the orchestration primitive, and why channels underdeliver', duration: '' },
          { title: 'Always-on routines: a GitHub issues monitor for treg, Slack alert triage, and a daily wiki health check from X bookmarks', duration: '' },
          { title: 'The toolkit around it: AgentMail inboxes, treg for pay-per-use data APIs, cookie sync over Tailscale, Peekaboo', duration: '' },
        ]
      },
      {
        section: '08/16 - Ara: Cloud Coding, Verified PRs, and Token Maxing',
        description: 'Ara founder Adi Singh on moving a codebase to the cloud with its secrets and CLIs intact, then shipping without a code review bottleneck: every PR carries a before and after session link as proof, and each merged fix spawns a follow-up check against real usage data.',
        lessons: 7,
        duration: '1h',
        lessonDetails: [
          { title: 'A cloud runtime needs the repo, the plugins (CLIs, MCPs, skills) and their secrets; a local daemon imports all three', duration: '' },
          { title: 'Kill the review bottleneck: the agent links the exact session, log lines or screenshot showing the fix worked, before and after', duration: '' },
          { title: 'Make the product agent-native: every button and surface reachable by Playwright, CLI or MCP, so agents can test what they build', duration: '' },
          { title: 'A hook at session end turns a one-off fix into a permanent worker that re-checks the change against real usage a day and a week later', duration: '' },
          { title: 'A triage agent that reviews every unmerged PR daily and spins up a fix for whatever blocked it', duration: '' },
          { title: 'Drop a link to a cool release in a Slack channel and a tested PR is waiting in a scratch workspace when you are back at your desk', duration: '' },
          { title: 'Token maxing: push the most code your budget allows, even on cheaper models, since a cheap model today beats a frontier model from two months ago', duration: '' },
        ]
      },
      {
        section: '08/02 - Graph Engineering Without a Framework',
        description: 'One term covering three different problems, and the two ways to actually enforce a graph: control flow written as code, or an SOP written into a skill the model runs. Plus the design patterns for cutting a job into agent nodes, and three graphs running in production.',
        lessons: 6,
        duration: '1h',
        lessonDetails: [
          { title: 'Three things people call graph engineering: control graph, knowledge graph, and graph of loops', duration: '' },
          { title: 'Why the term arrived: loops and orchestration both leave you supervising more work from further away', duration: '' },
          { title: 'You already run graphs: the goal feature with a stop hook, AutoResearch as prose, deep research out of the box', duration: '' },
          { title: 'Code-as-graph vs LLM-as-graph, and the session-resume limit that decides which one to reach for', duration: '' },
          { title: 'Cutting a graph into nodes: model-capability boundaries, what to codify, a schema per node, state as markdown plus an append-only log', duration: '' },
          { title: 'Three production graphs: daily design triage as a skill plus scripts, a three-phase ship-code workflow, and a batch API catalog', duration: '' },
        ]
      },
      {
        section: '07/26 - Vibe Coding From Your Phone, and a Loop on Reddit',
        description: "Member session: an Android client that drives Claude Code on a personal VPS by voice, so software gets built in the gaps a real life leaves. Plus Jason's Reddit-karma loop, on a channel he had failed at manually.",
        lessons: 6,
        duration: '1h',
        lessonDetails: [
          { title: 'Voice to VPS to Claude Code over Tailscale, demoed live from a forest on 5G', duration: '' },
          { title: 'The member arc: 13 years of Android, four kids, zero desk hours, first app shipped while in a supermarket', duration: '' },
          { title: 'Build the outer layer, rent the executor: no SDK and no framework, with Claude Code as the interchangeable engine', duration: '' },
          { title: 'Failure infrastructure: a lessons-learned hook on first-try failures, a tunnel watchdog, a 60-second heartbeat, a confidence score', duration: '' },
          { title: 'The delivery loop that closes on the same device: build, notify, install the APK, confirm', duration: '' },
          { title: "Jason's Reddit-karma loop: 6 to roughly 91 karma in six days, shipped as a free Loopany template", duration: '' },
        ]
      },
      {
        section: '07/19 - Running a Fleet of Parallel Agents with Orca',
        description: 'Orca founder Jing on running a fleet of parallel coding agents: cross-agent orchestration through the Orca CLI, taming token cost with multi-model routing, and a live demo of parallel worktrees, design mode, and automated PR review.',
        lessons: 8,
        duration: '1h',
        lessonDetails: [
          { title: 'State of parallel agents: token spend turned into a KPI ($9k burned overnight) and one-day turnaround expectations', duration: '' },
          { title: 'Loop engineering: chop a big goal into phases, run adversarial sub-agent review until the plan is clean, then implement', duration: '' },
          { title: 'Figure 7 to figure 8: stop running the agents yourself, put a master agent in charge of the fleet', duration: '' },
          { title: 'Live demo: one-shot a feature with cross-agent orchestration (Codex plans, Grok reviews, Opus implements, Grok validates with screenshots, then opens a PR)', duration: '' },
          { title: 'Orca CLI orchestration internals: worktrees, task dispatch, an inbox background thread, and the parent agent still making every decision', duration: '' },
          { title: 'Design mode for UI: grab DOM elements, annotate, freeze-and-draw, and feed the edits into the running agent', duration: '' },
          { title: 'Scaling and cost: agents are memory intensive so spread 100 across remote machines, and route cheap work (translation) to a free model to save Claude tokens', duration: '' },
          { title: 'Q&A: graph engineering, end-to-end test fixing, and why open source eases enterprise procurement', duration: '' },
        ]
      },
      {
        section: '07/12 - Anatomy of a Good Loop + Hermes Personal Automation',
        description: 'A member demos Hermes, a personal executive-assistant agent, then Jason breaks down what makes a loop trustworthy: the contract, four trigger types, the verifier, and the self-improving evolve step.',
        lessons: 9,
        duration: '1h',
        lessonDetails: [
          { title: 'Hermes: 7am start-day and end-day crons that sort your day into kill, defer, delegate', duration: '' },
          { title: 'Hermes meeting-prep: research a stranger from a local Obsidian LLM-wiki while you drive', duration: '' },
          { title: 'Hermes sync-sweep: reconcile notes and tasks across Obsidian, Notion, and Google', duration: '' },
          { title: 'Loop anatomy in one Markdown file: contract (goal, boundaries, SOP) plus state plus append-only log', duration: '' },
          { title: 'The four trigger types: continuous for-loop, cron, event/webhook, and the combo trigger', duration: '' },
          { title: 'Combo trigger: a cheap script checks for real work before waking the agent, to save tokens', duration: '' },
          { title: 'Role split for high-stakes work: orchestrator finds work, executor ships in an isolated worktree, verifier proves it', duration: '' },
          { title: 'The evolve step: a loop rewrites its own contract and triggers, moving deterministic work to scripts to get cheaper over time', duration: '' },
          { title: 'Loopany open-sourced, plus a team tool registry for sharing keys and skills without exposing the keys', duration: '' },
        ]
      },
      {
        section: '07/05 - Pi Agent Deep-Dive: Customizable Agent Harnesses',
        description: "Jason's deep-dive on Pi Agent (pygen): a minimal coding harness you fully customize through extensions, where the agent can write its own extension and hot-reload the harness it is running in.",
        lessons: 8,
        duration: '1h',
        lessonDetails: [
          { title: 'Why Pi is different: a minimal harness (only bash, read, write, edit) that adapts to you instead of you adapting to it', duration: '' },
          { title: 'Old harness tricks die when the model improves: the scratchpad and batch tools now baked into Sonnet 4 and 4.5', duration: '' },
          { title: 'The extension system: register tools, commands, hooks, UI, and model providers so nothing stays behind a black box', duration: '' },
          { title: 'The agent writes its own extension and hot-reloads the harness it is running in (live weather-widget demo)', duration: '' },
          { title: 'The package catalog: install MCP, go, browser and computer-use, or a full Codex feature set on demand', duration: '' },
          { title: 'pi-hijack: cleaning bash output before it hits the context window to cut tokens 80 to 90%', duration: '' },
          { title: 'Building custom extensions: git-context injection, custom tools, and a small-model permission gate for team access', duration: '' },
          { title: 'Building products on the coding-agent SDK: the five packages, reusing your subscription, and an open-Postia replica', duration: '' },
        ]
      },
      {
        section: '06/28 - Codebase Memory MCP + Postia Teardown',
        description: 'A pure-programmatic code graph that cuts agent token cost ~50% via a grep hook, plus a full teardown of Postia, an agent that builds and runs a whole business.',
        lessons: 8,
        duration: '1h',
        lessonDetails: [
          { title: 'Codebase Memory MCP: a pure-programmatic call graph with no LLM, free and local, built in seconds', duration: '' },
          { title: 'The pre-tool-use hook: a normal grep silently returns graph context, ~50% fewer tokens, no forced MCP tool', duration: '' },
          { title: 'Graph-native tools: get_architecture, search_graph, trace_path, query_graph, and cross-repo indexing', duration: '' },
          { title: 'Live A/B: same trace task at ~36K tokens without the graph vs ~11K with it', duration: '' },
          { title: 'Postia teardown: token-maxing onboarding, one email spins up a researched site, tweets, and ads', duration: '' },
          { title: "Postia's infra trick: site, email, and ads all run from Postia's own accounts for zero-setup launch", duration: '' },
          { title: 'Chat agent vs executor split for security isolation and per-task model cost control', duration: '' },
          { title: 'Outer-loop value: build on an existing harness (Pi Agent / pygen) and own the state, memory, and cron', duration: '' },
        ]
      },
      {
        section: '06/21 - Crabbox: Isolated Sandboxes for Parallel Agent Testing',
        description: 'Give every parallel agent its own isolated cloud sandbox that syncs only the dirty diff, tests in seconds, and returns video evidence to the PR.',
        lessons: 10,
        duration: '1h',
        lessonDetails: [
          { title: 'Merge is the new bottleneck: parallel agents flood you with PRs, so reviewing and merging replaces writing code as the constraint', duration: '' },
          { title: 'Codebase harness plus video evidence: every PR carries agent-recorded proof it works, via Playwright CLI', duration: '' },
          { title: 'Why local parallelism breaks: a shared local Supabase, hardcoded ports, and one PR schema change breaking every other test environment', duration: '' },
          { title: 'Crabbox core: warm up a cloud box, run any bash command that syncs only the dirty diff and looks local, then stop and delete to save cost', duration: '' },
          { title: 'Setup: a base Docker image, the crabbox file, env-key SSH sync, and a Daytona snapshot', duration: '' },
          { title: 'Artifacts: predefined screenshot and video commands that auto-download evidence or publish it to S3 for the PR', duration: '' },
          { title: 'Live walkthrough: warm up the box, run setup, and tunnel the cloud dev server back to localhost over SSH', duration: '' },
          { title: 'Agent-run end-to-end tests: each session spins its own isolated box, tests with Playwright CLI, and returns a video to the PR', duration: '' },
          { title: 'Loops on the Max plan: a local daemon-managed loop system, and an evolve step that rewrites triggers so loops get cheaper over time', duration: '' },
          { title: 'Multiplayer loop visibility: read teammates running loops and agent traces from one synced place across Codex, OpenClaw, and Claude Code', duration: '' },
        ]
      },
      {
        section: '06/14 - Loop Engineering Best Practices',
        description: 'Replacing manual prompting with self-running agent loops across support, SEO, growth, and ads - Claude Code on local machines with cron triggers.',
        lessons: 5,
        duration: '1h',
        lessonDetails: [
          { title: 'Three-layer architecture: typed artifact folders, per-loop README contracts, global logs.md', duration: '' },
          { title: 'Artifacts: typed output folders, each with a README defining schema, lifecycle, and exclusions', duration: '' },
          { title: 'Loop contracts: per-loop goal, workflow, backlog, and timeline so any agent can resume', duration: '' },
          { title: 'Open vs closed loops: closed loops verify their own fixes next cycle, so improvements compound', duration: '' },
          { title: 'Worked example: support agent finds export bug, triggers a coding agent, checks recurrence', duration: '' },
        ]
      },
      {
        section: '06/07 - Harness Engineering: Legible, Executable, Verifiable Repos',
        description: 'Make your repo legible, executable, and verifiable before running serious agentic workloads.',
        lessons: 6,
        duration: '1h',
        lessonDetails: [
          { title: 'Legible: sub-100-line CLAUDE.md as an index, detailed docs in /docs, constraints as lint rules', duration: '' },
          { title: 'Executable: one dev-server script, configurable port for parallel work trees, state-switching helpers', duration: '' },
          { title: 'Verifiable: Playwright CLI test videos on PRs, a PR skill that spawns a verifier sub-agent and loops', duration: '' },
          { title: 'Writing better goal prompts: scope, verification method, guardrails, stop conditions', duration: '' },
          { title: 'Scheduled autonomous tasks with /loop, and keeping tools atomic as models improve', duration: '' },
          { title: "Real examples: SuperDesign's 52-line CLAUDE.md, ESLint import-boundary rules, tmux dev server", duration: '' },
        ]
      },
      {
        section: '05/31 - Voice Agent Error Rates & Production Iteration',
        description: 'Building a calendar-booking voice agent with Dogra + Claude MCP, and taming voice AI error rates in production.',
        lessons: 7,
        duration: '1h',
        lessonDetails: [
          { title: 'Live demo: calendar booking voice agent using Dogra + Claude MCP integration', duration: '' },
          { title: 'Voice AI error rates across the STT to LLM to TTS cascade (20-25% per turn)', duration: '' },
          { title: 'Multi-agent workflow architecture for complex conversations without hallucination', duration: '' },
          { title: 'Speech-to-speech models eliminating latency bottlenecks (300ms vs 400ms+ cascade)', duration: '' },
          { title: 'Production deployment: call tracing, QA nodes, and iteration loops', duration: '' },
          { title: 'Open-source self-hosting with Docker Compose for data ownership and customization', duration: '' },
          { title: 'Market metrics: $5-6k/mo tickets for small business, $40-50k for mid-size', duration: '' },
        ]
      },
      {
        section: '05/24 - EachLabs CTO: From Model Chaos to Unified Workflows',
        description: 'How EachLabs unifies 600+ AI models behind a single API, and why multi-model workflows persist.',
        lessons: 7,
        duration: '1h',
        lessonDetails: [
          { title: 'Unifying 600+ AI models through a single API with no-markup pricing', duration: '' },
          { title: 'Why concurrency limits and provider management kill productivity', duration: '' },
          { title: 'Workflow demo: turning any product into trending social media videos', duration: '' },
          { title: 'The business model behind AI gateways and volume discount sharing', duration: '' },
          { title: 'Failover architecture: providers go down 80-90% more than traditional SaaS', duration: '' },
          { title: 'Documentation strategy for AI agent consumption vs human reading', duration: '' },
          { title: 'Vertical AI trends and why multi-model workflows persist despite omni-models', duration: '' },
        ]
      },
      {
        section: '05/17 - AI Wiki Toolkit: Eliminating Agent Memory Loss',
        description: 'A folder-based system for durable agent knowledge that compounds across sessions and repos.',
        lessons: 5,
        duration: '1h',
        lessonDetails: [
          { title: '/memories for accumulated solutions, /drafts auto-promoted after 3x reuse, /metrics for usage', duration: '' },
          { title: 'System-level vs repo-level separation for cross-project knowledge', duration: '' },
          { title: 'Integration: scans repos for agents.md / claude.md and injects prompts with folder references', duration: '' },
          { title: 'Index system linking to reference files (inspired by the Hermes skills structure)', duration: '' },
          { title: 'Routing system to pick the right markdown from 100+ memories and evaluate effectiveness', duration: '' },
        ]
      },
      {
        section: '05/10 - AI CTO Architecture: Orchestrating Agents Across Isolated VPS',
        description: 'Anicet (ascii.dev) demos an AI CTO that delegates to coding agents running on isolated cloud desktops.',
        lessons: 4,
        duration: '1h',
        lessonDetails: [
          { title: 'Persistent CTO agent delegates tasks, monitors progress, and aggregates reports', duration: '' },
          { title: 'Each task agent gets an isolated Hetzner VPS Linux desktop with Chrome, VS Code, full SSH', duration: '' },
          { title: '60fps desktop streaming via a forked Moonlight (originally built for game streaming)', duration: '' },
          { title: 'CTO agent stays persistent in the cloud, controlled via desktop app and Telegram bot', duration: '' },
        ]
      },
      {
        section: '05/03 - OpenAI Symphony + Automated Testing Setup',
        description: "OpenAI's Symphony open-source scheduler for coding agents, plus a harness-ready testing setup.",
        lessons: 7,
        duration: '1h',
        lessonDetails: [
          { title: 'Symphony as an open-source scheduler for coding agents', duration: '' },
          { title: 'Linear integration with workflow.md files for automated task management', duration: '' },
          { title: 'Making codebases harness-ready with end-to-end testing', duration: '' },
          { title: 'Playwright CLI for automated video evidence generation', duration: '' },
          { title: 'Isolated workspaces per ticket/task', duration: '' },
          { title: 'Scaling from interactive sessions to autonomous task completion', duration: '' },
          { title: 'Token usage and parallel-execution tradeoffs; status flow + human review', duration: '' },
        ]
      },
      {
        section: '04/26 - Open Organizational AI with PKA and Departmental Agents',
        description: "Simon Wallace-Jones's production system coordinating 36 codebases and 17+ dev tasks/week autonomously.",
        lessons: 8,
        duration: '1h',
        lessonDetails: [
          { title: 'Personal Knowledge Assistant (PKA) architecture coordinating AI across many repos', duration: '' },
          { title: 'Standardized AI DevOps folder structure with Git subtrees for consistent agent deployment', duration: '' },
          { title: 'Python scheduler with failure recovery and working-hours awareness', duration: '' },
          { title: 'Three-tier memory management (hot/warm/cold) following the Karpathy model', duration: '' },
          { title: 'Repo-specific agents with clean interface boundaries for permissions and knowledge isolation', duration: '' },
          { title: 'MicroSpec Document (MSD) lifecycle controlling tasks from planning to execution', duration: '' },
          { title: 'Local-first with Postgres for full visibility; Go-based cross-platform scheduler distribution', duration: '' },
          { title: 'Budget controls and watchdog agents to prevent runaway execution', duration: '' },
        ]
      },
      {
        section: '04/12 - Voice-to-Letter Pipeline: Agent Memory + Physical Mail',
        description: "Anders Kitson's voice app that turns spoken words into physical mailed letters, rebuilt in 2 days.",
        lessons: 5,
        duration: '1h',
        lessonDetails: [
          { title: '11Labs voice agents for conversation + Deepgram WebSockets for real-time speech-to-text', duration: '' },
          { title: 'PostGrid API for physical letter printing and delivery', duration: '' },
          { title: 'Railway for WebSocket deployment (working around Vercel limitations)', duration: '' },
          { title: 'Memory via local database context injection', duration: '' },
          { title: 'Intuitive tutorial onboarding flow', duration: '' },
        ]
      },
      {
        section: '03/29 - OpenClaw Production Guide: Real Business Workflows',
        description: 'Advanced OpenClaw for real business workflows, beyond basic tutorials.',
        lessons: 5,
        duration: '1h',
        lessonDetails: [
          { title: 'Setup: local vs VPS (Mac Mini + Tailscale vs fly.io), Slack teams, Resend email identities', duration: '' },
          { title: 'Memory in 3 levels: semantic search, lossless Claw plugin, custom domain folders + bootstrap hooks', duration: '' },
          { title: 'Web access: youtube-dlp transcripts, reader.jina.ai scraper, Reddit .json, GitHub CLI/RSS', duration: '' },
          { title: 'Business integration: support triage + PRs, ads optimization, sales segmentation & outreach', duration: '' },
          { title: 'Claude Code via SAP protocol, hook-based permissions, cost optimization via model routing', duration: '' },
        ]
      },
      {
        section: '03/22 - How I Use OpenClaw Internally',
        description: 'An inside look at how OpenClaw is used internally for day-to-day AI-powered workflows.',
        lessons: 1,
        duration: '1h'
      },
      {
        section: '03/15 - From Probabilistic to Production: Multi-AI Agent Workflows',
        description: 'Learn how to move multi-AI agent systems from experimental to production-grade reliability.',
        lessons: 1,
        duration: '1h'
      },
      {
        section: '03/01 - Building Context Infrastructure for Agent Collaboration',
        description: 'Explore how to build robust context infrastructure that enables multiple agents to collaborate effectively.',
        lessons: 1,
        duration: '1h'
      },
      {
        section: '02/22 - Toolhouse.AI: No-Code Agent Building',
        description: 'Build powerful AI agents without writing code using Toolhouse.AI\'s no-code platform.',
        lessons: 1,
        duration: '1h'
      },
      {
        section: '02/15 - GitContextController, OneContext, File-Based Memory',
        description: 'Master advanced context management tools including GitContextController, OneContext, and file-based memory systems.',
        lessons: 1,
        duration: '1h'
      },
      {
        section: '02/08 - Production-Scale Code AI: AugmentCode Deep-Dive',
        description: 'A comprehensive deep-dive into AugmentCode for production-scale AI-assisted software development.',
        lessons: 1,
        duration: '1h'
      },
      {
        section: '01/25 - Building ChatCut: Video Editor Agent Systems',
        description: 'Learn how to build ChatCut, an AI-powered video editor using agent-based systems.',
        lessons: 1,
        duration: '1h'
      },
      {
        section: '01/18 - Long-Running Coding Agents & MCP Context Optimization',
        description: 'Techniques for building long-running coding agents and optimizing context with MCP.',
        lessons: 1,
        duration: '1h'
      },
      {
        section: '01/11 - Context Engineering: Learning from Manus',
        description: 'Explore context engineering best practices and lessons learned from building the Manus agent.',
        lessons: 1,
        duration: '1h'
      }
    ]
  },
  {
    id: 'cursor-prompt-templates',
    title: 'Prompt Engineering Masterclass – Master System Prompts, Agent Patterns, and Decision Frameworks',
    subtitle: 'Prompt Engineering Masterclass',
    category: 'AI Agent Development',
    description: "Master prompt engineering from core techniques to system prompt design: Chain of Thought, ReAct, and the 12 Laws of System Prompts.",
    fullDescription: 'Go beyond basic prompting. This comprehensive masterclass covers everything from zero-shot and few-shot techniques to advanced agent system prompt design. You\'ll learn the 12 Laws of System Prompts used by production AI agents, problem-solving frameworks like MECE decomposition and 5WHY analysis, decision-making models including game theory and prospect theory, and advanced techniques like RAG, function calling, and multi-agent collaboration. Includes production-ready templates for auth, payments, and full-stack features.',
    icon: '🧠',
    image: '/thumbnails/prompt-engineering.png',
    duration: '8h+',
    level: 'All levels',
    students: '1,500+',
    lessons: '50',
    modules: [
      'Updated Templates (2026)',
      'Core Prompting Techniques',
      'Problem-Solving Frameworks',
      'Decision-Making Models',
      'System Prompt Design (12 Laws)',
      'Advanced Techniques'
    ],
    curriculum: [
      {
        section: 'Updated Templates (2026)',
        description: 'Production-ready prompt templates for auth, payments, database schemas, and full-stack features.',
        lessons: 4,
        duration: '45m',
        lessonDetails: [
          { title: 'Auth Template', duration: '12m' },
          { title: 'Payment Template', duration: '12m' },
          { title: 'Database Schema Template', duration: '10m' },
          { title: 'Full-Stack Feature Template', duration: '11m' },
        ]
      },
      {
        section: 'Core Prompting Techniques',
        description: 'Master the foundational prompting patterns: zero-shot, few-shot, chain-of-thought, and advanced reasoning strategies.',
        lessons: 9,
        duration: '1h 30m',
        lessonDetails: [
          { title: 'Zero-Shot Prompting', duration: '10m' },
          { title: 'Few-Shot Prompting', duration: '10m' },
          { title: 'Role-Based Prompting', duration: '10m' },
          { title: 'Chain of Thought', duration: '10m' },
          { title: 'Tree of Thought', duration: '10m' },
          { title: 'Self-Consistency', duration: '10m' },
          { title: 'ReAct', duration: '10m' },
          { title: 'Directional Stimulus', duration: '10m' },
          { title: 'Multimodal CoT', duration: '10m' },
        ]
      },
      {
        section: 'Problem-Solving Frameworks',
        description: 'Structured frameworks for breaking down complex problems and generating better LLM outputs.',
        lessons: 10,
        duration: '1h 30m',
      },
      {
        section: 'Decision-Making Models',
        description: 'Mental models and decision frameworks applied to prompt engineering for better reasoning.',
        lessons: 11,
        duration: '1h 45m',
      },
      {
        section: 'System Prompt Design (12 Laws)',
        description: 'The 12 laws of system prompt design for building reliable, consistent AI behavior.',
        lessons: 12,
        duration: '2h',
      },
      {
        section: 'Advanced Techniques',
        description: 'Cutting-edge prompt engineering techniques for production AI systems.',
        lessons: 7,
        duration: '1h',
      },
    ]
  },
  {
    id: 'cursor-docs',
    title: 'Ultimate Cursor IDE Course - Complete Developer Guide',
    subtitle: 'Complete Cursor IDE Mastery',
    category: 'AI Development',
    description: "Comprehensive Cursor IDE training: AI-powered coding, advanced features, shortcuts, and best practices for developers mastering AI coding.",
    fullDescription: 'Master every aspect of Cursor IDE with this comprehensive training program. From basic setup to advanced AI-powered development workflows, you\'ll learn all the features, shortcuts, and best practices that make Cursor the most powerful AI-assisted development environment available.',
    icon: '🎯',
    image: '/thumbnails/2cursor docs.jpeg',
    duration: '4 weeks',
    level: 'Beginner to Advanced',
    students: '2,100+',
    lessons: '28',
    modules: [
      'Cursor IDE Setup and Configuration',
      'Basic AI-Powered Coding Workflows',
      'Advanced Features and Shortcuts',
      'Code Generation and Refactoring',
      'Debugging with AI Assistance',
      'Version Control Integration',
      'Team Collaboration Features',
      'Productivity Optimization Techniques'
    ],
    curriculum: [
      {
        section: 'Section 1: Getting Started with Cursor',
        description: 'Learn the basics of Cursor IDE setup and initial configuration.',
        lessons: 7,
        duration: '1h 30m'
      },
      {
        section: 'Section 2: AI-Powered Development',
        description: 'Master AI-assisted coding, code generation, and intelligent suggestions.',
        lessons: 15,
        duration: '4h 15m'
      },
      {
        section: 'Section 3: Advanced Features',
        description: 'Explore advanced Cursor features, shortcuts, and productivity tools.',
        lessons: 6,
        duration: '2h 10m'
      }
    ]
  },

  {
    id: 'build-production-llm-apps',
    title: 'Build Production LLM Apps – Multimodal, Real-Time, Mobile & Agentic Workflows with OpenAI, Gemini & SAM',
    subtitle: 'Build Production LLM Apps',
    category: 'AI App Development',
    description: 'Learn to build powerful production-ready LLM apps with real-time APIs, mobile inference, multimodal models, and agentic workflows.',
    fullDescription: 'This advanced course shows you how to build production-grade LLM applications using cutting-edge tools like OpenAI structured outputs, Gemini 2.0 multimodal APIs, and real-time streaming interfaces. You\'ll explore use cases including web scraping, dynamic UI generation, external doc readers using CAG, and even deploying local models on mobile (iOS/Android). Build AI agents like the O1 Data Scientist, SAM2 face pixelation AI, and create real-time, multimodal commerce apps with Streamlit, Florence, and MLX. Perfect for developers ready to ship real-world LLM apps at scale.',
    icon: '🚀',
    image: '/thumbnails/buildproductionllmapps.png',
    duration: '8 weeks',
    level: 'All levels',
    students: '1,200+',
    lessons: '21',
    modules: [
      'OpenAI Structured Outputs',
      'CAG vs RAG Implementation',
      'Gemini 2.0 Multimodal eCommerce App',
      'Build O1 Data Scientist',
      'Real-time API Integration',
      'Build SAM2 AI Face Pixelation',
      'Run Local Models on Mobile'
    ],
    curriculum: [
      {
        section: 'Section 1: OpenAI Structured Output',
        description: 'Master OpenAI structured outputs for reliable data extraction and processing. Learn web scraping, dynamic UI generation, and external document processing.',
        lessons: 4,
        duration: '2h 30m',
        lessonDetails: [
          { title: 'Structure output 101', duration: '30m' },
          { title: 'Example 1: Web scraping', duration: '45m' },
          { title: 'Example 2: Video to blog agentic workflow', duration: '40m' },
          { title: 'Example 3: Dynamic UI generation', duration: '35m' }
        ]
      },
      {
        section: 'Section 2: CAG vs RAG',
        description: 'Compare CAG and RAG approaches for document processing. Build external doc readers using CAG methodology.',
        lessons: 2,
        duration: '1h 45m',
        lessonDetails: [
          { title: 'CAG example', duration: '50m' },
          { title: 'Build External doc reader MCP using CAG', duration: '55m' }
        ]
      },
      {
        section: 'Section 3: Gemini 2.0 Multimodal eCommerce App',
        description: 'Build a complete multimodal eCommerce application using Gemini 2.0 API with video, image, and text processing.',
        lessons: 3,
        duration: '2h 15m',
        lessonDetails: [
          { title: 'Gemini 2.0 Experimental API', duration: '40m' },
          { title: 'Wan 2.1 model', duration: '45m' },
          { title: 'Build streamlit app', duration: '50m' }
        ]
      },
      {
        section: 'Section 4: Build O1 Data Scientist',
        description: 'Create an AI data scientist agent that can analyze data and provide insights autonomously.',
        lessons: 1,
        duration: '45m',
        lessonDetails: [
          { title: 'Build O1 Data scientist', duration: '45m' }
        ]
      },
      {
        section: 'Section 5: Real-time API',
        description: 'Implement real-time streaming APIs for live data processing and user interactions.',
        lessons: 1,
        duration: '30m',
        lessonDetails: [
          { title: 'OpenAI Realtime API walkthrough', duration: '30m' }
        ]
      },
      {
        section: 'Section 6: Build SAM2 AI',
        description: 'Build AI applications using SAM2 for advanced computer vision tasks including face pixelation, video processing, and image manipulation.',
        lessons: 5,
        duration: '3h 20m',
        lessonDetails: [
          { title: 'Video tutorial', duration: '45m' },
          { title: 'Install models', duration: '25m' },
          { title: 'Use florence & SAM 2 to pixelate faces', duration: '50m' },
          { title: 'Process video', duration: '40m' },
          { title: 'Export & reset', duration: '40m' }
        ]
      },
      {
        section: 'Section 7: Run Local Model on Mobile',
        description: 'Deploy and run local LLM models on mobile devices (iOS/Android) using MLX and optimized inference techniques.',
        lessons: 4,
        duration: '2h 45m',
        lessonDetails: [
          { title: 'Android - Termux', duration: '45m' },
          { title: 'iOS - MLX on device inference', duration: '50m' },
          { title: 'iOS - Build chat history with templates', duration: '40m' },
          { title: 'iOS - Load any huggingface models', duration: '30m' }
        ]
      }
    ]
  },
  {
    id: 'mcp-course',
    title: 'MCP 101 – Build and Deploy with Model Context Protocol using fastMCP, Auth, and Stripe',
    subtitle: 'Model Context Protocol (MCP) 101',
    category: 'LLM System Engineering',
    description: 'Learn how to build and test Model Context Protocols (MCPs) using fastMCP, Claude, Cloudflare, and Stripe—ideal for AI engineers and app builders.',
    fullDescription: 'This course is your hands-on guide to Model Context Protocol (MCP)—a powerful way to structure, deploy, and interact with language models using modular, inspectable contexts. You\'ll explore MCP marketplaces, test flows using Claude + Inspector, and examine production-ready MCPs like the Playwright MCP. The second half of the course shows how to build your own MCP using tools like fastMCP, Cloudflare Workers, custom auth systems, and Stripe payments. Great for AI developers building reliable, inspectable agent workflows.',
    icon: '🔧',
    image: '/thumbnails/mcp.png',
    duration: '9 min',
    level: 'All levels',
    students: '800+',
    lessons: '14',
    relatedBlog: {
      href: '/blog/mcp-101-build-mcp-servers',
      label: 'free MCP 101 guide',
    },
    modules: [
      'MCP Marketplace & Installation',
      'Testing MCP in Inspector & Claude',
      'Awesome MCPs',
      'Playwright MCP',
      'Build Your Own MCP using fastMCP',
      'Build Your Own MCP with Cloudflare',
      'Build Auth System for MCP',
      'Connect Stripe Payment for MCP'
    ],
    curriculum: [
      {
        section: 'MCP Fundamentals & Ecosystem',
        description: 'Learn the fundamentals of Model Context Protocol, explore MCP marketplaces, and test MCP workflows using Claude Inspector.',
        lessons: 4,
        duration: '9 min',
        lessonDetails: [
          { title: 'MCP marketplace & installation', duration: '2m' },
          { title: 'How to test MCP in inspector & claude', duration: '3m' },
          { title: 'Awesome MCPs', duration: '2m' },
          { title: 'Playwright MCP', duration: '2m' },
        ]
      },
      {
        section: 'Build Your Own MCP Server',
        description: 'Build your own Model Context Protocol using fastMCP, Cloudflare Workers, implement authentication systems, and integrate Stripe payments.',
        lessons: 4,
        duration: '15 min',
        lessonDetails: [
          { title: 'Build your own MCP using fastMCP', duration: '4m' },
          { title: 'Build your own MCP with Cloudflare', duration: '4m' },
          { title: 'How to build auth system for MCP', duration: '4m' },
          { title: 'How to connect Stripe payment for MCP', duration: '3m' },
        ]
      },
      {
        section: 'Advanced Tool Calling Patterns',
        description: 'Advanced MCP patterns: Shadcn/Figma MCP integration, programmatic tool calls, dynamic filtering, tool search, interactive MCP apps, and production deployment with Streamable HTTP & FastMCP v3.',
        lessons: 6,
        duration: '45m',
        lessonDetails: [
          { title: 'Shadcn MCP & Figma MCP', duration: '8m' },
          { title: 'Programmatic Tool Call', duration: '7m' },
          { title: 'Dynamic Filtering & Tool Use Example', duration: '8m' },
          { title: 'Tool Search', duration: '6m' },
          { title: 'MCP Apps: Build Interactive UIs Inside Conversations', duration: '8m' },
          { title: 'Streamable HTTP & FastMCP v3: Production MCP Deployment', duration: '8m' },
        ]
      },
    ]
  },
  {
    id: 'agent-skills-course',
    title: 'Agent Skills 101 – Extend Any AI Agent with Reusable Skills, From Claude Code to Cursor',
    subtitle: 'Agent Skills 101',
    category: 'AI Agent Development',
    description: "Learn to install, create, and deploy Agent Skills, the open standard for packaging domain expertise into workflows for 50+ AI agents.",
    fullDescription: 'Agent Skills are the open standard for extending AI coding agents with specialized capabilities. Adopted by Claude Code, Cursor, Codex, GitHub Copilot, and 50+ more agents, Skills package domain knowledge, multi-step workflows, and best practices into portable folders any agent can follow. This course takes you from understanding what Skills are and how they differ from MCP and Rules, through installing and discovering Skills across the ecosystem, to building your own from scratch. You\'ll learn the SKILL.md spec, progressive loading architecture, cross-agent compatibility, security best practices, and how to iterate Skills into reliable playbooks. Whether you\'re using Claude Code, Cursor, or any compatible agent — this course gives you everything you need to 10x your agent workflows.',
    icon: '🧩',
    image: '/thumbnails/agent-skills.png',
    duration: '1h 30m',
    level: 'All levels',
    students: '400+',
    lessons: '25',
    author: 'Jason Zhou',
    authorTitle: 'Founder, AI Builder Club',
    authorAvatar: '/jason-zhou.png',
    relatedBlog: {
      href: '/blog/agent-skills-best-practices-guide',
      label: 'free Agent Skills best-practices guide',
    },
    modules: [
      'What Are Agent Skills & Architecture',
      'Skills vs MCP vs Rules',
      'Installing & Discovering Skills',
      'Skills in Claude Code & Cross-Agent Compatibility',
      'Skills in Cursor',
      'Curated Agent Skills Showcase',
      'SKILL.md Spec & YAML Frontmatter',
      'Build Your Own Skill from Scratch',
      'Testing, Debugging & Iterating',
      'Security Considerations & Best Practices'
    ],
    curriculum: [
      {
        section: 'Theory: What Are Agent Skills',
        description: 'Understand the Agent Skills open standard and how progressive loading works.',
        lessons: 4,
        duration: '25m',
        lessonDetails: [
          { title: 'What Are Skills?', duration: '' },
          { title: 'What Do Skills Look Like?', duration: '' },
          { title: 'The Core Mechanism of Skills', duration: '' },
          { title: 'Skills vs MCP vs Rules — When to Use What', duration: '' },
        ]
      },
      {
        section: 'Theory: Skills VS MCP',
        description: 'The critical distinction between Skills, MCP, and Rules - and when to use what.',
        lessons: 2,
        duration: '20m',
        lessonDetails: [
          { title: "What's Wrong with MCP?", duration: '' },
          { title: 'Will MCP Be Replaced?', duration: '' },
        ]
      },
      {
        section: 'Practice: Finding & Using Skills',
        description: 'Install and discover Skills across the ecosystem, set up Skills in Claude Code and Cursor.',
        lessons: 4,
        duration: '25m',
        lessonDetails: [
          { title: 'Where to Find Skills', duration: '' },
          { title: 'How to Use & Create Skills', duration: '' },
          { title: 'Installing Skills with the Vercel CLI', duration: '' },
          { title: 'Curated Agent Skills', duration: '' },
        ]
      },
      {
        section: 'Hands-On: Knowledge Retrieval Skill',
        description: 'Build a knowledge retrieval skill from scratch with progressive loading and cross-agent compatibility.',
        lessons: 2,
        duration: '30m',
        lessonDetails: [
          { title: 'Building a Universal Knowledge Retrieval Skill', duration: '' },
          { title: 'Turning Doc Sites into Knowledge Bases', duration: '' },
        ]
      },
      {
        section: 'Hands-On: Deconstructing Claude Design',
        description: 'Deconstruct a real-world skill used in production to understand architecture patterns.',
        lessons: 4,
        duration: '25m',
        lessonDetails: [
          { title: 'What Is Claude Design?', duration: '' },
          { title: "Breaking Down Claude Design's System Prompt", duration: '' },
          { title: "Extracting Claude Design's Essence into a Skill", duration: '' },
          { title: 'Real-World Comparison: How Much Does a Skill Help?', duration: '' },
        ]
      },
      {
        section: 'Best Practices from Anthropic',
        description: 'Official best practices and recommendations from Anthropic for building robust agent skills.',
        lessons: 3,
        duration: '25m',
        lessonDetails: [
          { title: 'The Nine Types of Skills', duration: '' },
          { title: 'Practical Tips for Building Skills', duration: '' },
          { title: 'Distributing & Managing Skills', duration: '' },
        ]
      },
      {
        section: 'Building Your Own Skill',
        description: 'Master the SKILL.md spec, YAML frontmatter, testing, debugging, iteration, and security.',
        lessons: 6,
        duration: '35m',
        lessonDetails: [
          { title: 'SKILL.md Spec & YAML Frontmatter', duration: '' },
          { title: 'Cross-Agent Compatibility', duration: '' },
          { title: 'Testing, Debugging & Iterating on Skills', duration: '' },
          { title: 'Security Considerations', duration: '' },
          { title: 'Build a Complete Skill from Scratch', duration: '' },
          { title: 'Build Your Own CLI with Printing Press', duration: '' },
        ]
      },
      {
        section: 'Previous Edition (Deprecated)',
        description: 'Earlier version of the course content, kept for reference.',
        lessons: 11,
        duration: '20m',
        lessonDetails: [
          { title: '[Deprecated] What Are Agent Skills?', duration: '' },
          { title: '[Deprecated] Skills vs MCP vs Rules – When to Use What', duration: '' },
          { title: '[Deprecated] Skills in Claude Code & Cross-Agent Compatibility', duration: '' },
          { title: '[Deprecated] Skills in Cursor', duration: '' },
          { title: '[Deprecated] Installing & Discovering Skills', duration: '' },
          { title: '[Deprecated] Curated Agent Skills', duration: '' },
          { title: '[Deprecated] SKILL.md Spec & YAML Frontmatter', duration: '' },
          { title: '[Deprecated] Testing, Debugging & Iterating on Skills', duration: '' },
          { title: '[Deprecated] Security Considerations & Best Practices', duration: '' },
          { title: '[Deprecated] Build a Complete Skill from Scratch', duration: '' },
          { title: '[Deprecated] Build Your Own CLI with Printing Press', duration: '' },
        ]
      },
    ]
  },
  {
    id: 'ai-agent-course',
    title: 'AI Agent 101 – Build and Deploy Research Agents with Tool Use, API Access, and Deep Web Crawling',
    subtitle: 'AI Agent 101',
    category: 'AI Agent Development',
    description: 'Learn how to build autonomous research agents using agentic workflows, tool calls, web scraping, and deep search—perfect for developers and data researchers.',
    fullDescription: 'This course is your crash course in AI agent development, covering everything from tool calling and agentic workflows to deploying agents as APIs. You\'ll build a powerful research agent step-by-step using a web scraping template, core open-source packages, and handle real-world challenges like login-required websites and deep research replication. The course wraps up with building a production-ready O1 Data Scientist Agent. You\'ll get hands-on experience with scalable frameworks, scraping strategies, and building agents that work across the web and APIs.',
    icon: '🤖',
    image: '/thumbnails/agent.png',
    duration: '4h 50m',
    level: 'All levels',
    students: '950+',
    lessons: '13',
    relatedBlog: {
      href: '/blog/ai-agents',
      label: 'free AI Agents 2026 guide',
    },
    modules: [
      'Agentic Workflows & Tool Calling',
      'Research Agent Development',
      'Web Scraping & API Integration',
      'Deep Web Crawling Techniques',
      'Agent Deployment & Production',
      'O1 Data Scientist Agent',
      'Scalable Agent Frameworks',
      'Real-World Agent Challenges'
    ],
    curriculum: [
      {
        section: 'Agent crash course',
        description: 'Learn the fundamentals of AI agent development, from agentic workflows to tool calling and API deployment.',
        lessons: 4,
        duration: '1h 30m',
        lessonDetails: [
          { title: 'Agentic workflow', duration: '25m' },
          { title: 'Tool calls', duration: '20m' },
          { title: 'Build research agent', duration: '30m' },
          { title: 'Deploy your agent as API', duration: '15m' }
        ]
      },
      {
        section: 'Build research agent',
        description: 'Build a comprehensive research agent with web scraping capabilities, handling complex scenarios and deep research tasks.',
        lessons: 8,
        duration: '3h 5m',
        lessonDetails: [
          { title: 'Intro', duration: '10m' },
          { title: '1 - Agentic web scraper template', duration: '25m' },
          { title: '1 - Understand core packages we use', duration: '20m' },
          { title: '1 - Code breakdown', duration: '15m' },
          { title: '2 - Web agent template', duration: '25m' },
          { title: '2 - Compare prices across e-commerce', duration: '30m' },
          { title: '2 - Tackle website requires login', duration: '25m' },
          { title: '3 - O3 deep research replica', duration: '35m' }
        ]
      },
      {
        section: 'Build O1 Data scientist agent',
        description: 'Create a production-ready O1 Data Scientist Agent capable of advanced data analysis and insights.',
        lessons: 1,
        duration: '15m',
        lessonDetails: [
          { title: 'O1 Data Scientist Agent', duration: '15m' }
        ]
      }
    ]
  },
  {
    id: 'coding-fundamental-course',
    title: 'Coding 101 – Launch Your First SaaS with HTML, CSS, JavaScript, and AI Tools',
    subtitle: 'Coding 101: Launch Your First SaaS',
    category: 'No-Code & Beginner Coding',
    description: 'Learn HTML, CSS, JavaScript, and build your first SaaS product—even if you\'ve never coded before. Perfect for makers and startup founders.',
    fullDescription: 'This beginner-friendly course is designed to help you launch your first SaaS product from scratch. You\'ll start with the fundamentals of HTML, CSS, and JavaScript, then dive into building your first MVP (Minimum Viable Product). Each section walks you through both frontend and backend development, ending with a deployable, real working app. Perfect for non-technical founders, indie hackers, and first-time developers looking to go from idea to a live product—no previous coding experience required.',
    icon: '💻',
    image: '/thumbnails/3beginnercourse.jpeg',
    duration: '12h 30m',
    level: 'All levels',
    students: '1,250+',
    lessons: '64',
    modules: [
      'Development Roadmap',
      'HTML Fundamentals',
      'CSS Styling & Design',
      'JavaScript Programming',
      'MVP Development',
      'Frontend Development',
      'Backend Development',
      'Full-Stack Integration'
    ],
    curriculum: [
      {
        section: 'Roadmap',
        description: 'Get started with your development journey and understand the path from zero to launching your first SaaS.',
        lessons: 1,
        duration: '30m',
        lessonDetails: [
          { title: 'Intro', duration: '30m' },
        ]
      },
      {
        section: 'HTML',
        description: 'Master HTML fundamentals and learn to structure web content with semantic markup.',
        lessons: 7,
        duration: '2h 45m',
        lessonDetails: [
          { title: 'Get started', duration: '25m' },
          { title: 'Structure', duration: '20m' },
          { title: 'Header', duration: '25m' },
          { title: 'Paragraphs & Semantic HTML', duration: '30m' },
          { title: 'Images', duration: '20m' },
          { title: 'Links', duration: '25m' },
          { title: 'Embedded Videos', duration: '20m' },
        ]
      },
      {
        section: 'CSS',
        description: 'Learn CSS styling to make your web pages beautiful, responsive, and well-structured with modern layout techniques.',
        lessons: 10,
        duration: '3h 30m',
        lessonDetails: [
          { title: 'CSS Basics', duration: '25m' },
          { title: 'Colours', duration: '20m' },
          { title: 'Spacing', duration: '25m' },
          { title: 'Fonts', duration: '20m' },
          { title: 'Buttons', duration: '25m' },
          { title: 'Display', duration: '20m' },
          { title: 'Debugging', duration: '15m' },
          { title: 'Flexbox', duration: '20m' },
          { title: 'CSS Grid', duration: '20m' },
          { title: 'Responsive Design', duration: '20m' },
        ]
      },
      {
        section: 'Javascript',
        description: 'Learn JavaScript programming to add interactivity and functionality to your web applications.',
        lessons: 6,
        duration: '2h 15m',
        lessonDetails: [
          { title: 'JavaScript Basics', duration: '25m' },
          { title: 'Variables & Data Types', duration: '20m' },
          { title: 'Functions & Events', duration: '25m' },
          { title: 'DOM Manipulation', duration: '25m' },
          { title: 'API Integration', duration: '20m' },
          { title: 'Error Handling', duration: '20m' },
        ]
      },
      {
        section: 'Build your 1st MVP',
        description: 'Put everything together to build your first Minimum Viable Product.',
        lessons: 3,
        duration: '1h 30m',
        lessonDetails: [
          { title: 'MVP Planning & Design', duration: '30m' },
          { title: 'Core Features Implementation', duration: '35m' },
          { title: 'Testing & Validation', duration: '25m' },
        ]
      },
      {
        section: 'Frontend',
        description: 'Advanced frontend development techniques for building professional user interfaces.',
        lessons: 6,
        duration: '2h 30m',
        lessonDetails: [
          { title: 'Advanced CSS Layouts', duration: '25m' },
          { title: 'Responsive Design', duration: '25m' },
          { title: 'User Experience (UX)', duration: '20m' },
          { title: 'Frontend Frameworks', duration: '25m' },
          { title: 'State Management', duration: '20m' },
          { title: 'Performance Optimization', duration: '15m' },
        ]
      },
      {
        section: 'Backend',
        description: 'Comprehensive backend development covering server setup, databases, APIs, authentication, payments, and deployment.',
        lessons: 16,
        duration: '6h',
      },
      {
        section: 'Dynamic Routes',
        description: 'Build dynamic, data-driven pages with parameterized routing and server-side rendering.',
        lessons: 8,
        duration: '3h',
      },
      {
        section: 'Final Words',
        description: 'Wrap up and next steps for your coding journey.',
        lessons: 1,
        duration: '15m',
      },
    ]
  },
  {
    id: 'how-to-build-your-own-cursor',
    title: 'Build Cursor for X – Custom AI Coding Workflows with Vercel, Claude, and Cursor',
    subtitle: 'Build Cursor for X',
    category: 'AI App Development',
    description: 'Build your own AI-powered developer workflow using Cursor, Claude, and Vercel—perfect for engineers, indie hackers, and startup builders.',
    fullDescription: 'This course teaches you how to build your own AI agent workflow using Cursor, customized for your specific use case (X). You\'ll integrate Vercel AI SDK, implement auth flows, and connect your backend. In the second half, you\'ll go behind-the-scenes of LearnTube, using tools like Claude 3, NotebookLM, and Cursor to build and ship an AI-enhanced product from idea to production. Technologies covered include Vercel AI SDK, Claude Code, Render, and Tailwind UI. By the end, you\'ll have a flexible foundation for any AI coding workflow.',
    icon: '⚡',
    image: '/thumbnails/buildcursorforx.png',
    duration: '14 min',
    level: 'All levels',
    students: '750+',
    lessons: '9',
    modules: [
      'Vercel AI SDK Integration',
      'Authentication Implementation',
      'Backend Connection',
      'AI Agent Workflow Development',
      'Product Planning & Documentation',
      'UI Design & Implementation',
      'Code Implementation with Claude',
      'Deployment & Production'
    ],
    curriculum: [
      {
        section: 'Build cursor for X',
        description: 'Learn to build your own AI-powered developer workflow using Cursor, Vercel AI SDK, and authentication systems.',
        lessons: 4,
        duration: '8 min',
        lessonDetails: [
          { title: 'Vercel AI SDK & Setup Agentic app', duration: '2m' },
          { title: 'How to stream tool response with vercel AI SDK', duration: '3m' },
          { title: 'Build Auth', duration: '2m' },
          { title: 'Connect backend (WIP)', duration: '1m' }
        ]
      },
      {
        section: 'Vibe Coding Workflow Behind LearnTube',
        description: 'Go behind-the-scenes of LearnTube development using Claude 3, NotebookLM, and Cursor for complete product workflow.',
        lessons: 5,
        duration: '6 min',
        lessonDetails: [
          { title: 'Intro to LearnTube: Turn YouTube Tutorials into Actionable Plans', duration: '1m' },
          { title: 'Using NotebookLM + ChatGPT O3 for Product Planning and Documentation', duration: '2m' },
          { title: 'V0 UI Design: From Idea to Interface', duration: '1m' },
          { title: 'Code Implementation Using Claude Code + Cursor', duration: '1.5m' },
          { title: 'Deploying with Vercel and Render', duration: '0.5m' }
        ]
      }
    ]
  },
  {
    id: 'web-scraping-courses',
    title: 'Web Scraping 101 – Extract LinkedIn Data with Playwright, LLMs, and Anti-Bot Techniques',
    subtitle: 'Web Scraping 101',
    category: 'Data Automation & Scraping',
    description: 'Learn how to scrape LinkedIn data using Playwright, XPath/CSS selectors, and LLMs—while avoiding anti-bot detection. Ideal for growth hackers and builders.',
    fullDescription: 'This course teaches you how to scrape data from LinkedIn effectively and responsibly. You\'ll use Playwright to automate browser actions, handle login flows, and extract structured data using XPath/CSS selectors or LLMs. You\'ll also learn how to avoid anti-bot systems with smart setup tips. Ideal for developers, marketers, and researchers who want to build lead generation tools, data enrichment workflows, or personal research agents. Fast, focused, and practical.',
    icon: '🕷️',
    image: '/thumbnails/webscraping.png',
    duration: '12 min',
    level: 'All levels',
    students: '650+',
    lessons: '6',
    modules: [
      'Playwright Automation',
      'LinkedIn Data Extraction',
      'Login Flow Handling',
      'CSS/XPath Selectors',
      'LLM-Based Data Extraction',
      'Anti-Bot Detection Avoidance',
      'Data Enrichment Workflows',
      'Lead Generation Tools'
    ],
    curriculum: [
      {
        section: 'How Playwright works',
        description: 'Understanding the fundamentals of Playwright automation.',
        lessons: 1,
        duration: '2m 12s'
      },
      {
        section: 'How to handle login?',
        description: 'Managing authentication flows in web scraping.',
        lessons: 1,
        duration: '1m 39s'
      },
      {
        section: 'Extract data via CSS/XPath selectors',
        description: 'Using selectors to extract structured data from web pages.',
        lessons: 1,
        duration: '3m 7s'
      },
      {
        section: 'Extract data via LLM',
        description: 'Leveraging LLMs for intelligent data extraction.',
        lessons: 1,
        duration: '2m 27s'
      },
      {
        section: 'How to setup things properly to avoid anti-bot?',
        description: 'Best practices for avoiding anti-bot detection systems.',
        lessons: 1,
        duration: '1m 45s'
      }
    ]
  },
  {
    id: 'claude-code-course',
    title: 'Claude Code 101 – Ship Real Products with 3 Guided Labs, the Template Vault, and the 2026 Workflow',
    subtitle: 'Claude Code 101 (Rebuilt June 2026)',
    category: 'AI Development',
    description: "Claude Code course with 3 guided Labs: a live website, a full-stack app with payments, and business automation. Rebuilt June 2026.",
    fullDescription: 'Theory is free on our blog. This course is where you ship: 3 guided Labs take you from a live website, to a full-stack app with Stripe payments, to a business automation that pays for the course in saved hours. Around the Labs sits the full 2026 workflow - CLAUDE.md, skills, hooks, subagents, parallel agents, and AFK autonomy - practiced on a real open-source codebase, not toy demos. Every config ships in the Template Vault, and the course covers what most others recorded before existed: auto mode, agent view, dynamic workflows, computer use. Rebuilt June 2026, updated continuously.',
    icon: '🖥️',
    image: '/thumbnails/claudecode.png',
    duration: '7h+',
    level: 'All levels',
    students: '600+',
    lessons: '27',
    relatedBlog: {
      href: '/blog/claude-code',
      label: 'free Claude Code course',
    },
    modules: [
      '3 Guided Labs: Website, Full-Stack App, Automation',
      'CLAUDE.md, Auto Memory & Progressive Disclosure',
      'Plan Mode, PRDs & Tracer Bullets',
      'Skills, Hooks & Custom Commands',
      'Subagents & the 4 Parallelism Surfaces',
      'AFK Autonomy: Sandboxes, Ralph Loops, Backlogs',
      'Token Economics & Security 80/20',
      'The Template Vault (starter kit included)'
    ],
    curriculum: [
      {
        section: 'Quick Start (skip if you\'re already fluent)',
        description: 'Setup, model selection, permission modes, and the 10 terminal moves that compound across every session.',
        lessons: 3,
        duration: '35m',
        lessonDetails: [
          { title: 'Start Here: What You\'ll Ship', duration: '8m' },
          { title: 'Setup That Doesn\'t Fight You', duration: '12m' },
          { title: 'Terminal Fluency: 10 Moves You\'ll Use Every Session', duration: '15m' },
        ]
      },
      {
        section: 'Part 1 – Steering Claude',
        description: 'The highest-leverage files: LLM constraints, CLAUDE.md\'s four roles, auto memory, the .claude directory, token economics, and the improvement loop.',
        lessons: 5,
        duration: '1h 20m',
        lessonDetails: [
          { title: 'The Four Constraints That Explain Every Bad Session', duration: '15m' },
          { title: 'CLAUDE.md That Earns Its Tokens (+ Auto Memory)', duration: '18m' },
          { title: 'Inside .claude: Scopes, Rules, and Progressive Disclosure', duration: '16m' },
          { title: 'Context and Token Economics', duration: '17m' },
          { title: 'The Improvement Loop: /init, /insights, and the Boris Cherny Workflow', duration: '14m' },
        ]
      },
      {
        section: 'Part 2 – Plan & Build: The Labs',
        description: 'Plan mode, PRDs and tracer bullets, research and prototyping - then ship a live website and a full-stack app with payments.',
        lessons: 6,
        duration: '2h 10m',
        lessonDetails: [
          { title: 'Plan Mode and the Plan-Execute-Clear Loop', duration: '16m' },
          { title: 'Massive Tasks: PRDs, Multi-Phase Plans, and Tracer Bullets', duration: '18m' },
          { title: 'Research and Prototyping: Where Your Taste Matters', duration: '14m' },
          { title: 'Lab 1: Ship a Live Website with the Screenshot Loop', duration: '30m' },
          { title: 'Lab 2a: Full-Stack App – Spec, Plan, Auth, Schema', duration: '30m' },
          { title: 'Lab 2b: Payments, Deploy, Iterate to Done', duration: '25m' },
        ]
      },
      {
        section: 'Part 3 – Extend & Automate',
        description: 'Commands, skills, MCP economics, hooks as quality gates, subagents - and a business automation Lab with measured 10x token savings.',
        lessons: 7,
        duration: '1h 50m',
        lessonDetails: [
          { title: 'Custom Commands, /goal, and /loop', duration: '13m' },
          { title: 'Skills: Progressive Disclosure as a Feature', duration: '16m' },
          { title: 'Lab 3: A Business Automation Skill (Email Labeler)', duration: '28m' },
          { title: 'MCP and Plugins: the Token Overhead Nobody Audits', duration: '14m' },
          { title: 'Hooks: Deterministic Quality Gates', duration: '15m' },
          { title: 'Subagents: Context Isolation and the Patterns That Pay', duration: '17m' },
          { title: 'Beyond the Repo: Automation Tiers and Scheduled Work', duration: '15m' },
        ]
      },
      {
        section: 'Part 4 – Autonomy & Scale',
        description: 'Feedback loops, deep modules, the four parallelism surfaces, AFK autonomy with Ralph loops and sandboxes, security, and your Claude Code operating system.',
        lessons: 6,
        duration: '1h 40m',
        lessonDetails: [
          { title: 'Feedback Loops That Make Agents Trustworthy', duration: '17m' },
          { title: 'Codebases AI Loves: Deep Modules', duration: '15m' },
          { title: 'Running Agents in Parallel: the Four Surfaces', duration: '17m' },
          { title: 'AFK Autonomy: Sandboxes, Ralph Loops, and Backlogs', duration: '20m' },
          { title: 'Security 80/20 for Builders', duration: '15m' },
          { title: 'Your Claude Code Operating System', duration: '14m' },
        ]
      },
    ]
  },
  {
    id: 'openclaw-course',
    title: 'OpenClaw 101 – Build and Deploy Your Own Self-Hosted AI Agent with OpenClaw',
    subtitle: 'OpenClaw 101',
    category: 'AI Agent Development',
    description: "Learn OpenClaw, the open-source AI agent framework with 365K+ GitHub stars: install, configure, and connect any LLM to 20+ messaging platforms.",
    fullDescription: 'OpenClaw is the fastest-growing open-source AI agent framework of 2026, with 365K+ GitHub stars and 3M+ active users. It runs as a persistent daemon on your machine, connecting any LLM (Claude, GPT, Gemini, DeepSeek, local models via Ollama) to 23+ messaging platforms including WhatsApp, Telegram, Discord, Slack, Signal, and iMessage. This course walks you through the full setup: from installation and onboarding to configuring providers, building custom skills, setting up production deployments with Docker, and real-world business workflow automation. You\'ll learn how to turn OpenClaw into a 24/7 personal AI assistant that can execute shell commands, manage files, automate browsers, call APIs, and handle tasks autonomously.',
    icon: '🦀',
    image: '/thumbnails/openclaw.png',
    duration: '4h',
    level: 'All levels',
    students: '450+',
    lessons: '35',
    relatedBlog: {
      href: '/blog/ai-agents',
      label: 'free AI Agents 2026 guide',
    },
    modules: [
      'OpenClaw Architecture & Installation',
      'Provider Setup (Claude, GPT, Ollama)',
      'Messaging Platform Integration',
      'Skills & Skill Marketplace',
      'Custom Skill Development',
      'Production Deployment with Docker',
      'Security & Permissions',
      'Real Business Workflow Automation'
    ],
    curriculum: [
      {
        section: 'The AI Productivity Paradox',
        description: 'Why AI tools haven\'t delivered the productivity gains everyone expected, and the paradigm shift from chatbots to agents.',
        lessons: 2,
        duration: '25m',
        lessonDetails: [
          { title: 'The AI Productivity Paradox', duration: '12m' },
          { title: 'From Chatbots to Agents', duration: '13m' },
        ]
      },
      {
        section: 'OpenClaw Fundamentals',
        description: 'What makes OpenClaw different, the four pillars of its architecture, and why it changes everything.',
        lessons: 2,
        duration: '25m',
        lessonDetails: [
          { title: 'Why OpenClaw Changes Everything', duration: '12m' },
          { title: 'The Four Pillars', duration: '13m' },
        ]
      },
      {
        section: 'Installation & Provider Setup',
        description: 'Install OpenClaw, configure model providers (Claude, GPT, Gemini, Ollama), and get your first agent running.',
        lessons: 2,
        duration: '30m',
        lessonDetails: [
          { title: 'Installing OpenClaw', duration: '15m' },
          { title: 'Model Provider Setup', duration: '15m' },
        ]
      },
      {
        section: 'Identity & Persona Configuration',
        description: 'Configure the four identity files and build a security-hardened persona for production use.',
        lessons: 2,
        duration: '30m',
        lessonDetails: [
          { title: 'The Four Identity Files', duration: '15m' },
          { title: 'Building a Security-Hardened Persona', duration: '15m' },
        ]
      },
      {
        section: 'Memory Architecture',
        description: 'The dual-layer memory system, write triggers, retrieval strategies, and hybrid search.',
        lessons: 3,
        duration: '40m',
        lessonDetails: [
          { title: 'The Dual-Layer Memory Architecture', duration: '15m' },
          { title: 'Write Triggers, Retrieval & Hybrid Search', duration: '13m' },
          { title: 'Memory Best Practices', duration: '12m' },
        ]
      },
      {
        section: 'System Integration',
        description: 'Understand how the agent stack fits together, connect messaging channels, and configure file and browser operations.',
        lessons: 3,
        duration: '40m',
        lessonDetails: [
          { title: 'How the Agent Stack Fits Together', duration: '13m' },
          { title: 'Connecting Your First Channel', duration: '15m' },
          { title: 'File Operations & Browser Automation', duration: '12m' },
        ]
      },
      {
        section: 'Production & Best Practices',
        description: 'Common pitfalls to avoid and real-world business automation examples.',
        lessons: 2,
        duration: '25m',
        lessonDetails: [
          { title: 'Common Pitfalls', duration: '12m' },
          { title: 'Business Automation Examples', duration: '13m' },
        ]
      },
      {
        section: 'Skills Architecture',
        description: 'Advanced skills system: how skills work internally and how to install and manage them via ClawHub.',
        lessons: 2,
        duration: '30m',
        lessonDetails: [
          { title: 'Skills Architecture', duration: '15m' },
          { title: 'ClawHub: Installing & Managing Skills', duration: '15m' },
        ]
      },
      {
        section: 'Cron & Scheduling',
        description: 'Automate agent tasks with cron fundamentals and advanced scheduling patterns.',
        lessons: 2,
        duration: '30m',
        lessonDetails: [
          { title: 'Cron Fundamentals', duration: '15m' },
          { title: 'Advanced Cron', duration: '15m' },
        ]
      },
      {
        section: 'Multi-Agent Architecture',
        description: 'Why multi-agent beats single agent, routing and isolation patterns, and subagent orchestration.',
        lessons: 3,
        duration: '45m',
        lessonDetails: [
          { title: 'Why Multi-Agent Beats Single Agent', duration: '15m' },
          { title: 'Routing, Isolation & Multi-Agent Configuration', duration: '15m' },
          { title: 'Subagent Orchestration', duration: '15m' },
        ]
      },
      {
        section: 'Writing Agent & Document Delivery',
        description: 'Set up a writing agent for content creation and automate document delivery workflows.',
        lessons: 2,
        duration: '30m',
        lessonDetails: [
          { title: 'Writing Agent: Setup', duration: '15m' },
          { title: 'Document Delivery', duration: '15m' },
        ]
      },
      {
        section: 'Advanced Provider & Integration Patterns',
        description: 'Advanced provider configuration, custom integrations, and complex workflow patterns.',
        lessons: 3,
        duration: '40m',
      },
      {
        section: 'Performance & Optimization',
        description: 'Optimize agent performance, reduce latency, and tune for production workloads.',
        lessons: 2,
        duration: '25m',
      },
      {
        section: 'Advanced Deployment & Security',
        description: 'Production deployment patterns, security hardening, and monitoring for multi-agent systems.',
        lessons: 3,
        duration: '35m',
      },
      {
        section: 'Advanced Business Workflows',
        description: 'Complex real-world automation patterns combining skills, cron, multi-agent, and document delivery.',
        lessons: 2,
        duration: '25m',
      },
    ]
  },
  {
    id: 'finetune-llm-course',
    title: 'Finetune and Deploy Your Own LLM – OpenAI, Modal, and Llama Agent Workflows',
    subtitle: 'Finetune and Deploy Your Own LLM',
    category: 'LLM Training & Deployment',
    description: 'Learn how to finetune OpenAI models and deploy them to production using Modal—with bonus content on building your own Llama-based Slack agent.',
    fullDescription: 'This hands-on course walks you through finetuning and deploying your own LLM, starting with OpenAI finetuning, dataset preparation, and testing. You\'ll learn how to deploy your model to Modal for scalable inference and set up real-world use cases. The second section introduces Llama 3.1, including a Slack bot agent setup and required configuration. Whether you\'re working with OpenAI APIs or open-source LLMs like Llama, this course helps you turn models into production-ready tools fast.',
    icon: '🎯',
    image: '/thumbnails/finetune.png',
    duration: '3h 15m',
    level: 'All levels',
    students: '890+',
    lessons: '7',
    modules: [
      'OpenAI Finetuning Fundamentals',
      'Dataset Preparation & Training',
      'Model Deployment with Modal',
      'Production-Ready LLM Systems',
      'Llama 3.1 Integration',
      'Slack Agent Development',
      'Scalable Inference Setup'
    ],
    curriculum: [
      {
        section: 'Finetune 101',
        description: 'Learn the fundamentals of LLM finetuning, from data preparation to deployment on Modal for production use.',
        lessons: 5,
        duration: '2h 30m',
        lessonDetails: [
          { title: 'Finetune LLM Overview', duration: '25m' },
          { title: '1. Prep Training data', duration: '35m' },
          { title: '2. Finetune OpenAI model', duration: '45m' },
          { title: '3.1 How to deploy your finetuned LLM intro', duration: '20m' },
          { title: '3.2 Deploy finetuned LLM on modal', duration: '25m' }
        ]
      },
      {
        section: 'Train your own Llama (🚧 Content Coming Soon)',
        description: 'Build your own Llama 3.1 Slack agent with custom training and deployment workflows.',
        lessons: 2,
        duration: '45m',
        lessonDetails: [
          { title: 'Build Llama 3.1 Slack agent', duration: '35m' },
          { title: 'Requirements.txt', duration: '10m' }
        ]
      }
    ]
  },
  {
    id: 'agent-system-prompt-playbook',
    title: 'Agent System Prompt Playbook - The 12 Laws for Building Reliable Agent Prompts',
    subtitle: 'Agent System Prompt Playbook',
    category: 'AI Agent Development',
    description: 'A concise, actionable playbook for writing system prompts that make AI agents reliable, consistent, and production-ready.',
    fullDescription: 'Most agent failures trace back to the system prompt. This playbook distills the 12 laws of system prompt design into a hands-on course. You\'ll reverse-engineer real system prompts from production agents, learn the structural patterns that separate brittle prompts from robust ones, and build your own agent system prompt in the capstone. Whether you\'re building with Claude Code, Cursor, or your own agent framework, these laws apply everywhere.',
    icon: '📜',
    image: '/thumbnails/agent-prompt-playbook.png',
    duration: '1h',
    level: 'Intermediate',
    students: '200+',
    lessons: '6',
    relatedBlog: {
      href: '/blog/ai-agents',
      label: 'free AI Agents 2026 guide',
    },
    modules: [
      'Reverse-Engineering System Prompts',
      'The 12 Laws of System Prompt Design',
      'Capstone: Build Your Own Agent Prompt',
    ],
    curriculum: [
      {
        section: 'Start Here',
        description: 'Reverse-engineer a real production system prompt to understand what makes agent prompts work.',
        lessons: 1,
        duration: '15m',
        lessonDetails: [
          { title: 'Steal the System Prompt', duration: '15m' },
        ]
      },
      {
        section: 'The 12 Laws',
        description: 'The 12 structural laws for writing system prompts that make agents reliable and consistent.',
        lessons: 4,
        duration: '35m',
      },
      {
        section: 'Put It All Together',
        description: 'Apply all 12 laws to design your own production-ready agent system prompt.',
        lessons: 1,
        duration: '10m',
        lessonDetails: [
          { title: 'Capstone', duration: '10m' },
        ]
      },
    ]
  },
  {
    id: 'harness-101',
    title: 'Harness 101 – Engineering the Loop Around the Model',
    subtitle: 'Harness 101: Engineering the Loop Around the Model',
    category: 'AI Agent Development',
    description: 'An agent is a model plus a harness. The model is rented; the harness is the part you own. Learn the loop, tools, context, and memory that turn a rented model into a working agent.',
    fullDescription: 'Almost everything that looks like a different "advanced agent" - Deep Research, Plan-then-Act, coding agents, multi-agent systems - is the same ReAct loop with different restraints bolted on. This course peels that sentence apart one layer at a time: the loop, the tools (a tool is a contract, not a capability), context as a budget, memory that survives a reset, and the production scaffolding of skills, hooks, and prompt architecture. Every lesson is concept-first with an optional Build It appendix that assembles, across the course, one runnable agent in Python. Built for engineers who want to read, debug, and design any agent from first principles.',
    icon: '🪢',
    image: '/thumbnails/harness-101.png',
    duration: '5h+',
    level: 'Intermediate',
    students: 'New',
    lessons: '25',
    relatedBlog: {
      href: '/blog/ai-agents',
      label: 'free AI Agents 2026 guide',
    },
    modules: [
      'The ReAct Loop and Harness Anatomy',
      'Tools: A Tool Is a Contract',
      'Context Engineering: The Budget',
      'Memory That Survives a Reset',
      'Orchestration: From One Run to Many',
      'Skills, Hooks & Prompt Architecture',
      'Build It: One Runnable Agent in Python',
    ],
    curriculum: [
      {
        section: 'Module 0 – Orientation',
        description: 'What a harness is, and why it - not the model - is the product you own.',
        lessons: 1,
        duration: '15m',
        lessonDetails: [
          { title: 'What Is a Harness, and Why This Course', duration: '15m' },
        ]
      },
      {
        section: 'Module 1 – Foundations: The Loop',
        description: 'The ReAct loop as the one primitive, harness anatomy, Deep Research, and Plan-then-Act.',
        lessons: 4,
        duration: '45m',
        lessonDetails: [
          { title: 'The ReAct Loop: The One Primitive', duration: '11m' },
          { title: 'Anatomy of a Harness', duration: '11m' },
          { title: 'Many Turns: Deep Research and Who Decides to Stop', duration: '11m' },
          { title: 'Plan-then-Act: Giving the Loop Discipline', duration: '12m' },
        ]
      },
      {
        section: 'Module 2 – Tools: The Model\'s Hands',
        description: 'A tool is a contract written for the model, the quality of that contract, the built-in toolset, and the agent file system.',
        lessons: 4,
        duration: '48m',
      },
      {
        section: 'Module 3 – Context Engineering',
        description: 'Context as a metered budget, offloading to disk, compaction and microcompact, and progressive disclosure.',
        lessons: 4,
        duration: '48m',
      },
      {
        section: 'Module 4 – Memory & Knowledge',
        description: 'Why data is not memory, the three-layer memory model, and building a memory substrate that heals itself.',
        lessons: 3,
        duration: '36m',
      },
      {
        section: 'Module 5 – Orchestration: From One Run to Many',
        description: 'You are already a loop, open vs closed loops, ReAct to orchestration, and the loop\'s evolution.',
        lessons: 4,
        duration: '44m',
      },
      {
        section: 'Module 6 – Production: Extending & Shipping a Harness',
        description: 'Skills, hooks, and plugins; prompt architecture from a real system prompt; writing for agents; the frontier; and a capstone harness design.',
        lessons: 5,
        duration: '55m',
      },
    ]
  },
  {
    id: 'loop-engineering',
    title: 'Loop Engineering – Ship While You\'re Away',
    subtitle: 'Loop Engineering: Ship While You\'re Away',
    category: 'AI Agent Development',
    description: 'You have been running your agent by hand: prompt, wait, read, prompt again. You are the loop. This course teaches you to engineer the loop itself so the agent ships real work unattended.',
    fullDescription: 'When you type "do phase 2", wait, then type "do phase 3", you are the for loop. Loop Engineering hands that loop to the agent - safely. You build it in layers: feedback gates that earn the right to walk away, a sandbox that bounds the blast radius, the Ralph loop that runs unattended, a GitHub-issue backlog that doubles as the agent\'s memory, routing that keeps humans where taste lives, and a scheduled automation ladder. By the capstone you have a loop that wakes on schedule, pulls the top task off your backlog, ships a PR behind quality gates, and reports back. Every lesson ends with a copy-paste practice on your own repo. The practical sequel to Harness 101.',
    icon: '🔁',
    image: '/thumbnails/loop-engineering.png',
    duration: '4h+',
    level: 'Intermediate',
    students: 'New',
    lessons: '25',
    relatedBlog: {
      href: '/blog/claude-code',
      label: 'free Claude Code course',
    },
    modules: [
      'You Are the For Loop',
      'Feedback Gates: Earn the Right to Walk Away',
      'The Sandbox: Bounding the Blast Radius',
      'The Ralph Loop, Line by Line',
      'The Backlog: Issues as Queue and Memory',
      'Routing: Humans Where Taste Lives',
      'The Automation Ladder & the Daily Shipper',
    ],
    curriculum: [
      {
        section: 'Module 0 – Orientation',
        description: 'What you will ship, and the reframe that starts it: you are the for loop your agent runs inside.',
        lessons: 2,
        duration: '14m',
        lessonDetails: [
          { title: 'What You\'ll Ship', duration: '8m' },
          { title: 'You Are the For Loop', duration: '6m' },
        ]
      },
      {
        section: 'Module 1 – Feedback Gates: Earn the Right to Walk Away',
        description: 'Machine gates, not steering, are the license to go unattended: the ship skill, pre-commit enforcement, and a lab that proves the gate bites.',
        lessons: 4,
        duration: '44m',
        lessonDetails: [
          { title: 'Nobody Is Watching', duration: '10m' },
          { title: 'The Ship Skill: A Definition of Done', duration: '11m' },
          { title: 'Gates at the Door: Pre-Commit Enforcement', duration: '12m' },
          { title: 'Practice: Prove the Gate', duration: '11m' },
        ]
      },
      {
        section: 'Module 2 – The Sandbox',
        description: 'The blast radius of an unattended agent, choosing your isolation, and a lab that builds the agent container.',
        lessons: 3,
        duration: '30m',
      },
      {
        section: 'Module 3 – The Loop Itself',
        description: 'The Ralph loop dissected line by line, the loop prompt, observability for a run you did not watch, and your first unattended run.',
        lessons: 4,
        duration: '44m',
      },
      {
        section: 'Module 4 – The Backlog',
        description: 'Don\'t plan, queue: PRD to backlog, issues as the agent\'s memory, the pick rule, and a full pipeline lab.',
        lessons: 5,
        duration: '55m',
      },
      {
        section: 'Module 5 – Routing: Humans Where Taste Lives',
        description: 'The taste test for HITL vs AFK, the QA loop, and running two lanes - planning and implementation - over one repo at once.',
        lessons: 3,
        duration: '33m',
      },
      {
        section: 'Module 6 – Production: The Automation Ladder',
        description: 'From on-demand to scheduled autonomy, guardrails at scale, the Daily Shipper capstone, and compounding the system.',
        lessons: 4,
        duration: '48m',
      },
    ]
  },
] 